import type { ConditionKind, NodeLabel, Provenance } from '@atbash-labs/ontology-core'

/**
 * Plant Viewer types — moved here from
 * `domains/ontology-ui/state/src/api/types.ts` as part of the
 * ontology-api refactor. Every `Plant*` shape the Plant Viewer
 * consumes is declared here so the read-path (cypher + extractors
 * + transforms in this package) can stay framework-free and the
 * domain-side RTK Query slice only orchestrates fetch/cache.
 */

// ─── Cypher read wire shape ───────────────────────────────

/**
 * Local copy of the `/cypher/read` wire shape. The canonical
 * definition lives next to the drain helper in the state-package's
 * `ontology-api-base.ts` (and is re-exported there as
 * `GraphCypherQueryResult` for back-compat); we keep an identical
 * declaration here because this package can't import from state
 * (dep direction is state → plantview, not the other way) and the
 * mapper signatures in `transform/hierarchy.ts` need a type to bind
 * the `raw` argument against. Structurally identical, so values flow
 * across the boundary without coercion.
 */
export interface CypherReadResult {
  count: number
  results: Array<Record<string, unknown>>
}

export type PlantProvenance = Pick<
  Provenance,
  | 'source_file'
  | 'source_kind'
  | 'source_page'
  | 'source_region'
  | 'source_excerpt'
  | 'extraction_model'
  | 'extraction_method'
  | 'confidence'
  | 'evidence_items'
  | 'last_evidence_at'
>

export interface PlantEntityMeta {
  provenance?: PlantProvenance
  /** Absent means default-trusted, false means invalidated, and true means re-validated. */
  valid?: boolean
  /** Normalized confidence in the inclusive range [0, 1]. */
  confidence?: number
  /** Last ingest job that wrote this node (last-writer-wins). */
  lastIngestJobId?: string
}

export interface PlantEdge {
  type: string
  source: string
  target: string
  props: Record<string, unknown>
  provenance?: PlantProvenance
  confidence?: number
}

export interface PlantGenericEntity extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  [key: string]: unknown
}

export interface PlantEntityResult {
  labels: string[]
  entity: PlantGenericEntity
  /** Labels on this node for which it is currently a stray. */
  strayLabels: string[]
}

export type PlantConnectionDirection = 'in' | 'out'

export interface PlantEntityConnectionRow {
  peer: PlantGenericEntity
  peerLabels: string[]
  edge: PlantEdge
}

export interface PlantEntityConnectionGroup {
  type: string
  direction: PlantConnectionDirection
  total: number
  rows: PlantEntityConnectionRow[]
}

export interface PlantEntityConnectionsArgs {
  id: string
}

export interface PlantLabelCount {
  label: string
  total: number
}

export type PlantEntityValidityFilter = 'all' | 'invalidated' | 'trusted'

/** `'stray'` = only nodes missing every anchor pattern for the label. */
export type PlantEntityStrayFilter = 'all' | 'stray'

export interface PlantLabelEntitiesArgs {
  label: NodeLabel
  search?: string
  validity?: PlantEntityValidityFilter
  /** Ignored for stray-exempt labels (empty `ANCHOR_PATTERNS_BY_LABEL` entry). */
  stray?: PlantEntityStrayFilter
  minConfidence?: number
  page: number
  pageSize?: number
}

export interface PlantLabelEntitiesPage {
  label: NodeLabel
  total: number
  entities: PlantGenericEntity[]
  /** Ids on this page that match the label's stray predicate (empty when exempt). */
  strayIds: string[]
}

// =============================================================================
// Plant Viewer types
// =============================================================================

/**
 * Lightweight summary of a Site node, used by the Plant Viewer landing grid.
 * Mirrors `Site` properties on the Neo4j wire (id is the canonical ontology id;
 * `_id` is the Neo4j element id used by `/graph/nodes/{id}/...` endpoints).
 */
export interface PlantSite extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  description?: string
}

/** Lightweight Line summary used inside site detail. */
export interface PlantLine extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  line_code?: string
  description?: string
}

/** Lightweight Process summary used inside line / process detail. */
export interface PlantProcess extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  process_type?: string
  description?: string
  /**
   * Free-form input ports declared on the Process node (`packages/ontology/core/src/generated/types/nodes/process.ts`).
   * Stored as a flat string property array on the Neo4j node and surfaced
   * here via `extractNode`'s raw-properties spread. Used by the Processes
   * tab to render the I/O card.
   */
  inputs?: string[]
  /** Free-form output ports declared on the Process node. See {@link inputs}. */
  outputs?: string[]
}

/** Lightweight Equipment summary used inside line/equipment detail. */
export interface PlantEquipment extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  equipment_type?: string
  /**
   * Normalized concept name written by the ingest writer
   * (`packages/ontology/ingest/src/ingest-writer.ts`). Stored as a flat
   * top-level property on the Neo4j Equipment node and surfaced here via
   * `extractNode`'s raw-properties spread. Used by the plant viewer to
   * pick an OpenBridge symbol for the equipment node.
   */
  canonical_name?: string
  hierarchical_name?: string
  area_name?: string
  vendor?: string
  /** Free-form properties echoed for the equipment detail header. */
  properties?: Record<string, unknown>
}

/** Lightweight tag summary (ScadaTag or PLCTag) attached to an Equipment. */
export interface PlantTag extends PlantEntityMeta {
  id: string
  _id: string
  /**
   * Node label of the underlying graph entity. `Tag` / `PLCTag` are the
   * native leaf-reading shapes; `UDTInstance` shows up when an Observable
   * / Setpoint points at a whole UDT instance (Ignition-style) instead of
   * a flat tag — the live-polling layer treats UDTInstance entries as
   * non-pollable composite signals.
   */
  label: string
  name: string
  /**
   * Canonical Ignition tag path (`[Provider]Folder/Tag`). Stored on the
   * `:Tag` node post-UDT-ingest-refactor — both regular tags and
   * UDT-instance member tags emit it at ingest so live-polling consumers
   * can use it directly without recomposing `[provider]<name>` (which
   * doesn't work for UDT-instance members whose `name` is the
   * within-instance path only).
   */
  tag_path?: string
  /** Ignition tag-provider name (e.g. `default`); set on ScadaTag nodes. */
  provider_name?: string
  /** Folder portion of an Ignition tag path. Optional / informational. */
  folder_name?: string
  /** Leaf tag name (without folder). */
  tag_name?: string
  /** System whose namespace defines this tag. */
  tag_system?: string
  /** Ignition value-source kind (e.g. `opc`, `memory`). */
  ignition_tag_type?: string
  /** Physical OPC item metadata; never used as graph identity. */
  opc_item_path?: string
  /** Physical OPC NodeId metadata; never used as graph identity. */
  opc_node_id?: string
  data_type?: string
  unit?: string
}

/**
 * One member reading under a {@link PlantUdtInstance}. Backed by a
 * `UDTMember` node in the graph (`UDTInstance -[:HAS_MEMBER]-> UDTMember`).
 * `value` is the snapshot captured at structural ingest time — not live.
 */
export interface PlantUdtMember {
  id: string
  _id: string
  name: string
  /**
   * Ignition member kind (`memory`, `opc`, `derived`, …). Some members
   * (notably folder-only nodes) carry no `member_type`.
   */
  member_type?: string
  /** Snapshot value captured at structural ingest time, when present. */
  value?: string
  data_type?: string
  /** Engineering unit when the gateway exposed one for this member. */
  eng_unit?: string
  /**
   * Pollable Ignition tag path — e.g. `[default]SWAGE/SWAGER1/Running` —
   * built client-side from the parent UDT's `provider_name` + `name` and
   * this member's `name`. `undefined` when we don't have enough info to
   * construct one (the live panel skips members without a path).
   */
  pollPath?: string
}

/**
 * One `UDTInstance` materialised by an Equipment
 * (`Equipment -[:MATERIALIZES]-> UDTInstance`). Carries the template
 * identity (`name`, `type_id`, `provider_name`, `folder_name`) plus the
 * pre-fetched list of `UDTMember` leaves so the equipment detail page
 * can render a drill-down without a second round-trip.
 */
export interface PlantUdtInstance extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /** UDT definition this is an instance of, when set on the node. */
  type_id?: string
  /** Ignition tag-provider that owns this UDT instance (e.g. `default`). */
  provider_name?: string
  /** Folder portion of the UDT path, e.g. `SWAGE` for `SWAGE/SWAGER1`. */
  folder_name?: string
  /** Last path segment of the UDT instance, e.g. `SWAGER1`. */
  instance_name?: string
  /** Member readings under this UDT instance (snapshot values only). */
  members: PlantUdtMember[]
}

/** Resolved upstream gateway for an equipment (used to drive live polling). */
export interface PlantGatewayRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  gateway_url?: string
  gateway_type?: string
  /** Optional Ignition project hint (passed through to /live-data/tags). */
  project?: string
}

/**
 * `PlantSite` plus the per-site counts the Equipment tab landing grid
 * needs. Returned by `getPlantSites` so the list can render badges
 * ("4 lines · 38 equipment") without an N+1 follow-up.
 */
export interface PlantSiteSummary extends PlantSite {
  /** Number of top-level lines (`Site -[:HAS_LINE]-> Line`). */
  lineCount: number
  /**
   * Number of `Equipment` nodes reachable from this site via any line or
   * subline (`Site -[:HAS_LINE]-> Line -[:HAS_SUBLINE*0..]-> Line -[:HAS_EQUIPMENT]-> Equipment`).
   */
  equipmentCount: number
}

/**
 * `PlantLine` plus precomputed counts and the single Process this line
 * `INSTANTIATE`s. Used by the site detail line tiles and the line detail
 * page's subline list.
 */
export interface PlantLineSummary extends PlantLine {
  /** Direct subline count (`Line -[:HAS_SUBLINE]-> Line`). */
  sublineCount: number
  /**
   * Equipment reachable from this line including the equipment of all its
   * sublines. Mirrors the count used in the design's line tiles.
   */
  equipmentCount: number
  /**
   * Ids for the same equipment set as `equipmentCount`. Lets Plant session
   * pills count property updates (`~n`) without write-log containment edges.
   */
  equipmentIds?: string[]
  /**
   * Process that this line instantiates (`Line -[:INSTANTIATES]-> Process`).
   * Lines are required to instantiate exactly one process per the schema,
   * but we model it as nullable so the UI doesn't crash on partially
   * ingested data.
   */
  process: PlantProcess | null
}

/**
 * Compact Line row for chat suggested prompts. Names only — chat must
 * not invent "Line 2" when the graph has no such node.
 */
export interface PlantLineInventoryItem {
  id: string
  name: string
  equipmentCount: number
}

export interface PlantSiteDetail {
  site: PlantSite
  lines: PlantLineSummary[]
  /**
   * Directed `Line-[:FEEDS]->Line` edges between the lines on this site.
   * Used to render the connectivity diagram in `SiteDetailPage`.
   */
  feeds: PlantEdge[]
  /** True when this site matches none of its placement anchor patterns. */
  isStray: boolean
}

export interface PlantLineDetail {
  /** The line (or subline) being viewed. */
  line: PlantLine
  /** True when this line matches none of its placement anchor patterns. */
  isStray: boolean
  /** Optional site for breadcrumb context (always present for top-level lines). */
  site: PlantSite | null
  /**
   * Immediate parent line, set when this node is itself a subline
   * (`Line -[:HAS_SUBLINE]-> Line`). `null` for top-level lines.
   */
  parentLine: PlantLine | null
  /** The single Process the line `INSTANTIATE`s. */
  process: PlantProcess | null
  /** Direct sublines (`Line -[:HAS_SUBLINE]-> Line`), with summary counts. */
  sublines: PlantLineSummary[]
  /**
   * Directed `Line-[:FEEDS]->Line` edges between this line's sublines.
   * Used to render the subline flow on `LineDetailPage`.
   */
  sublineFeeds: PlantEdge[]
  /**
   * Directed `Equipment-[:CONNECTS_TO]->Equipment` edges among
   * {@link directEquipment}. Used to render the physical-connectivity
   * diagram on `LineDetailPage` (distinct from WIP `FEEDS`).
   */
  equipmentConnects: PlantEdge[]
  /**
   * Equipment hung directly off the line via `Line -[:HAS_EQUIPMENT]-> Equipment`.
   * Equipment owned by sublines is intentionally excluded — the design
   * surfaces it on each subline's own page.
   */
  directEquipment: PlantEquipment[]
  /**
   * Controlling PLC reached via `Line -[:CONTROLLED_BY]-> Controller`
   * (typically a line master controller). `null` when none is mapped.
   * Lets the detail page link straight into the PLC tab for the controller.
   */
  controller: PlantControllerRef | null
  /**
   * Telemetry-source computes serving this line, rolled up across the
   * line's own `MONITORED_BY` edge *and* every Equipment in its
   * `HAS_SUBLINE` subtree (deduped by id). Lets the detail page surface a
   * "Telemetry sources" section that links into the Compute tab — the
   * inverse of the Compute page's "Monitored equipment & lines" section.
   * Most `MONITORED_BY` edges sit at the equipment grain, so the roll-up
   * is what makes the line→compute link visible at all.
   */
  computes: PlantComputeRef[]
  /** Utility services this line owns via `Line -[:HAS_UTILITY]-> Utility`. */
  utilities: PlantUtilityRef[]
  /**
   * Procedures that apply to this line via inbound
   * `Procedure -[:PROCEDURE_FOR]-> Line`.
   */
  procedures: PlantProcedureRef[]
}

/**
 * One Equipment node inside a line's control-coverage roll-up, with the
 * PLC controllers that directly control it (`Equipment -[:CONTROLLED_BY]->
 * Controller`). Surfaced under its owning line/subline in
 * {@link PlantLineControlCoverage}.
 */
export interface PlantLineControlEquipment {
  id: string
  name: string
  type?: string
  controllers: PlantControllerRef[]
}

/**
 * One line/subline node in a line's control-coverage roll-up. The flat
 * list is stitched into a tree client-side via {@link parentId} so the
 * Line page can render the nested "Control coverage" view: each
 * line/subline carries its own direct controllers plus the equipment
 * (with their controllers) hung off it.
 */
export interface PlantLineControlNode {
  id: string
  name: string
  /**
   * Parent line within the viewed subtree (`Line -[:HAS_SUBLINE]-> Line`).
   * `null` for the root line/subline the page is rendered for.
   */
  parentId: string | null
  /** True for the line/subline the detail page is rendered for. */
  isRoot: boolean
  lineCode?: string
  /** Controllers directly controlling this line/subline. */
  controllers: PlantControllerRef[]
  /** Equipment hung off this line/subline, each with its own controllers. */
  equipment: PlantLineControlEquipment[]
}

/**
 * Control-coverage roll-up for a line and its full `HAS_SUBLINE` subtree.
 * Returned by `getPlantLineControlCoverage`. The page prunes branches
 * that reach no controller and renders the rest as a nested tree of
 * sublines → equipment → controllers.
 */
export interface PlantLineControlCoverage {
  lines: PlantLineControlNode[]
}

/**
 * One row in the global process tree returned by `getPlantProcessTree`.
 *
 * The tree is flat — `parentId` (set by the `<-[:HAS_SUBPROCESS]-` edge)
 * is the only thing the client needs to reconstruct the hierarchy. Each
 * row also carries the implementation count (lines + sublines that
 * `INSTANTIATE` this process) so the left-rail tree can show the
 * little count badge per node without an N+1 round-trip.
 */
export interface PlantProcessTreeNode {
  process: PlantProcess
  /** Parent process id via `(:Process)-[:HAS_SUBPROCESS]->(:Process)`, or `null` for roots. */
  parentId: string | null
  /**
   * Number of `Line` nodes (top-level **and** sublines) that
   * `INSTANTIATE` this process across all sites. Used by the tree node
   * count badge and the "implementations" chip on the detail header.
   */
  implementationCount: number
  /** Number of direct `HAS_SUBPROCESS` children. Lets the tree decide whether to render a caret. */
  subprocessCount: number
}

export interface PlantProcessTree {
  nodes: PlantProcessTreeNode[]
}

/**
 * One implementation of a Process at a Site: a Line (top-level) or a
 * Subline (`HAS_SUBLINE` descendant of a Line) that `INSTANTIATE`s the
 * process. Sublines carry their parent line for breadcrumb context
 * ("Mixer-7 · subline of Line 3").
 */
export interface PlantProcessImplementation {
  line: PlantLine
  /** The site that ultimately owns this implementation (via `Site -[:HAS_LINE]-> Line`). */
  site: PlantSite | null
  /**
   * Parent line for sublines. `null` means this implementation is a
   * top-level line (i.e. attached directly to the site via `HAS_LINE`).
   */
  sublineParent: PlantLine | null
}

export interface PlantProcessDetail {
  process: PlantProcess
  /** True when this process matches none of its placement anchor patterns. */
  isStray: boolean
  /** Parent process via `<-[:HAS_SUBPROCESS]-(:Process)`, or `null` for roots. */
  parent: PlantProcess | null
  /** Direct `HAS_SUBPROCESS` children (one level deep). */
  subprocesses: PlantProcess[]
  /** All lines + sublines that `INSTANTIATE` this process, across every site. */
  implementations: PlantProcessImplementation[]
}

/**
 * Classification of an equipment property node. Mirrors the labels
 * `Observable`, `Setpoint`, and `EquipmentProperty` defined in
 * `packages/ontology/core/src/generated/types/nodes/asset.ts`. The
 * pre-AI-pass `EquipmentProperty` collapses to `'property'` here; the
 * post-pass promotes them to `'observable'` or `'setpoint'`.
 */
export type PlantEquipmentPropertyKind = 'observable' | 'setpoint' | 'property'

/**
 * One property node attached to an Equipment, with its associated Tag
 * (when one is wired up). Observables and unclassified properties reach
 * a Tag via `MEASURED_BY`; setpoints reach a Tag via `SET_BY`.
 */
export interface PlantEquipmentProperty extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  kind: PlantEquipmentPropertyKind
  unit?: string
  range_low?: number
  range_high?: number
  description?: string
  /**
   * Tag that backs this property (`MEASURED_BY` for observables /
   * properties, `SET_BY` for setpoints). `null` if the property has no
   * tag wired up yet (common pre-AI-pass).
   */
  tag: PlantTag | null
}

export interface PlantUtilityRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /**
   * `electrical` | `compressed_air` | `water` | `steam` | `nitrogen` |
   * `vacuum` | `hydraulic` | `other` (and related ingest enums).
   */
  utility_kind?: string
}

/**
 * Full Utility node for the Utility entity page. Kind-specific props
 * (`voltage_class` / `water_kind` / `refrigerant_type`) ride the node;
 * per-consumer connection specs stay on the inbound edge.
 */
export interface PlantUtilityDetailNode extends PlantUtilityRef {
  utility_kind_other?: string
  voltage_class?: string
  water_kind?: string
  refrigerant_type?: string
  description?: string
}

/** One inbound `REQUIRES_UTILITY` consumer with typed edge props. */
export interface PlantUtilityConsumer {
  equipment: PlantEquipment
  edge: PlantEdge
}

/**
 * One-shot detail bundle for a Utility entity page
 * (`getPlantUtilityDetail`).
 */
export interface PlantUtilityDetail {
  utility: PlantUtilityDetailNode
  consumers: PlantUtilityConsumer[]
  /**
   * Factory breadcrumb context from `(Site|Line)-[:HAS_UTILITY]->`
   * ownership.
   */
  site: PlantSite | null
  line: PlantLine | null
  /** True when this utility matches none of its placement anchor patterns. */
  isStray: boolean
}

export interface PlantEquipmentDetail {
  equipment: PlantEquipment
  /** True when this equipment matches none of its placement anchor patterns. */
  isStray: boolean
  /** Tags reached from any property attached to this equipment. */
  tags: PlantTag[]
  /**
   * Property nodes attached to the equipment (Observable, Setpoint, or
   * pre-pass EquipmentProperty), each with its associated tag.
   */
  properties: PlantEquipmentProperty[]
  /**
   * UDT instances this equipment materialises
   * (`Equipment -[:MATERIALIZES]-> UDTInstance`), each with its leaf
   * `UDTMember` readings pre-fetched. Lets the equipment detail page
   * render Ignition-style template-typed gear (a `SWAGE/SWAGER1`
   * instance with its `Running`, `Count_Discharge`, … members) as a
   * standalone section, independently of whether the AI tag-linker
   * also wired an `Observable -[:MEASURED_BY]-> UDTInstance` edge.
   */
  udtInstances: PlantUdtInstance[]
  /** Utility vocabulary nodes required by this equipment. */
  utilities: PlantUtilityRef[]
  /** Typed `REQUIRES_UTILITY` relationships carrying connection specifications. */
  utilityEdges: PlantEdge[]
  /** First gateway reachable via Equipment <- Line <- Site <- Gateway. */
  gateway: PlantGatewayRef | null
  /** Optional site for breadcrumb context. */
  site: PlantSite | null
  /** Optional line for breadcrumb context. */
  line: PlantLine | null
  /**
   * Process that the parent line instantiates, when reachable. Lets the
   * detail page render a "↳ <process>" chip that cross-tabs into the
   * Processes view via `plantHref`.
   */
  process: PlantProcess | null
  /**
   * Controlling PLC reached via `Equipment -[:CONTROLLED_BY]-> Controller`.
   * `null` when no controller is mapped. Lets the detail page link straight
   * into the PLC tab for the controlling Controller.
   */
  controller: PlantControllerRef | null
  /**
   * Robot controllers that drive this robotic-cell equipment via
   * `RobotController -[:CONTROLS]-> Equipment`.
   */
  robotControllers: PlantRobotControllerRef[]
  /**
   * Computes whose telemetry this equipment is `MONITORED_BY` (the OPC
   * server / device hosting its tags). Lets the detail page link straight
   * into the Compute tab — the inverse of the Compute page's "Monitored
   * equipment & lines" section.
   */
  computes: PlantComputeRef[]
  /**
   * Ancestor equipment via inbound `HAS_EQUIPMENT`, rootward first.
   */
  parentEquipment: PlantEquipment[]
}

/**
 * Procedure node projected for equipment/line relationship panels.
 * Graph stores display text on `title`; extractors map that onto `name`.
 */
export interface PlantProcedureRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /** `inspection` | `changeover` | `cleaning` | `loto` | `sop` | … */
  procedure_kind?: string
  frequency?: string
  duration_minutes?: number
  loto_required?: boolean
}

/**
 * Full Procedure node for the Procedure entity page. Graph stores display
 * text on `title`; extractors map that onto `name` like {@link PlantProcedureRef}.
 */
export interface PlantProcedureDetailNode extends PlantProcedureRef {
  steps?: Record<string, unknown>[]
  ppe_required?: string[]
  authored_by?: string
  source_doc_id?: string
  source_doc_page?: number
  description?: string
}

/** One `PROCEDURE_FOR` target with Neo4j labels for grouping / PlantHref. */
export interface PlantProcedureAppliesTo {
  peer: PlantGenericEntity
  peerLabels: string[]
}

/** One `REQUIRES_PART` pairing with quantity / optional from the edge. */
export interface PlantProcedurePartRequirement {
  part: PlantPartRef
  quantity?: number
  isOptional?: boolean
}

/** One `REQUIRES_SKILL_LEVEL` pairing with grade range from the edge. */
export interface PlantProcedureSkillRequirement {
  role: PlantRoleRef
  minGrade?: number
  maxGrade?: number
}

/** Product chip for `APPLIES_TO_PRODUCT`. */
export interface PlantProductRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  product_code?: string
  category?: string
}

/**
 * One-shot detail bundle for a Procedure entity page
 * (`getPlantProcedureDetail`).
 */
export interface PlantProcedureDetail {
  procedure: PlantProcedureDetailNode
  appliesTo: PlantProcedureAppliesTo[]
  parts: PlantProcedurePartRequirement[]
  skills: PlantProcedureSkillRequirement[]
  products: PlantProductRef[]
  /**
   * Factory breadcrumb context resolved from the preferred PROCEDURE_FOR
   * subject (Equipment → Line → …). Null when the procedure is unanchored.
   */
  site: PlantSite | null
  line: PlantLine | null
  breadcrumbSubject: PlantProcedureAppliesTo | null
  /** True when this procedure matches none of its placement anchor patterns. */
  isStray: boolean
}

/**
 * Normalized step row from {@link normalizeProcedureSteps}. Opaque /
 * unshaped rows land as a single stringified `instruction`.
 */
export interface NormalizedProcedureStep {
  stepNumber?: number
  instruction: string
  details: Array<{ key: string; value: string }>
}

/** Root-cause node under a fault symptom (`FaultCause.cause` → `name`). */
export interface PlantFaultCauseRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /** Likelihood in [0, 1] when known. */
  probability?: number
  /**
   * How many Problems inducing the parent symptom carry a human-confirmed
   * `CAUSED_BY` to this cause. Absent for causes that reached the symptom
   * some other way (legacy ingest without Problems).
   */
  confirmations?: number
}

/** `EXHIBITS_SYMPTOM.status` ladder, weakest → strongest evidence. */
export type PlantExhibitsSymptomStatus = 'documented' | 'monitored' | 'observed'

/**
 * Symptom node (`FaultSymptom.symptom` → `name`) with nested causes, as
 * seen from one Equipment / Part through its `EXHIBITS_SYMPTOM` edge.
 */
export interface PlantFaultSymptomRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  kind?: ConditionKind
  severity?: string
  /** Edge status; absent on legacy rows written before the ladder existed. */
  exhibits_status?: PlantExhibitsSymptomStatus
  /** Provenance source for a documented edge, such as `manual` or `rounds_sheet`. */
  exhibits_source?: string
  first_observed_at?: string
  last_observed_at?: string
  causes: PlantFaultCauseRef[]
}

/** Lightweight MES entity chip (Recipe / Phase / Batch / Operation). */
export interface PlantMesEntityRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
}

/** Inbound MES usage families for one Equipment. */
export interface PlantMesUsageRefs {
  recipes: PlantMesEntityRef[]
  phases: PlantMesEntityRef[]
  batches: PlantMesEntityRef[]
  operations: PlantMesEntityRef[]
}

/**
 * Below-the-fold relationship bundle for an Equipment page
 * (`getPlantEquipmentRelationships`). Kept separate from
 * {@link PlantEquipmentDetail} so the heavy header query stays small
 * and these panels can fail/load independently.
 */
export interface PlantEquipmentRelationships {
  bomParts: PlantPartRef[]
  bomEdges: PlantEdge[]
  /** True when the BOM exceeded the 500-part display cap after depth 8. */
  bomTruncated: boolean
  /**
   * Direct child equipment via outbound `HAS_EQUIPMENT`.
   * Kept separate from `bomParts` so tooling and Part BOM stay distinct.
   */
  subEquipment: PlantEquipment[]
  connections: PlantEquipment[]
  procedures: PlantProcedureRef[]
  faults: PlantFaultSymptomRef[]
  areas: PlantArea[]
  mesUsage: PlantMesUsageRefs
}

/**
 * HumanLabor contribution projected for line labor panels.
 * Under LineOperation graphs this is participation inside an operation;
 * under legacy graphs it is still the path node.
 */
export interface PlantHumanLaborRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  description?: string
  operation_kind?: string
  /**
   * Legacy ad-hoc sort key on HumanLabor. New graphs put order on
   * `LineOperation.sequence_number` instead; kept so unmigrated data still sorts.
   */
  sequence_number?: number
  ad_hoc?: boolean
  /** Capacity-sheet timing props when present (schema'd under a separate ticket). */
  cycle_time_s?: number
  manual_time_s?: number
  auto_time_s?: number
  walk_time_s?: number
  pieces_per_cycle?: number
}

/** Concrete ordered step on a Line/Subline (`LineOperation`). */
export interface PlantLineOperationRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  description?: string
  /** Execution/display order among primary operations on the owning Line. */
  sequence_number?: number
  operation_kind?: string
  inputs?: string
  outputs?: string
  ad_hoc?: boolean
}

/** Equipment linked to a LineOperation via `USES_EQUIPMENT`. */
export interface PlantOperationEquipment {
  equipment: PlantEquipment
  /** `source | destination | tool | fixture | processor | inspection | other` */
  usageKind?: string
}

/**
 * One HumanLabor contribution under a LineOperation (or a legacy station
 * when the graph has not been migrated yet).
 */
export interface PlantOperationLabor {
  task: PlantHumanLaborRef
  numEmployees?: number
  staffing: PlantLaborStaffing[]
  schedules: PlantScheduleProfileRef[]
  trigger: PlantLaborTrigger | null
  procedures: PlantProcedureRef[]
}

/** One primary LineOperation with its labor/equipment participation. */
export interface PlantLineOperation {
  operation: PlantLineOperationRef
  labor: PlantOperationLabor[]
  equipment: PlantOperationEquipment[]
  procedures: PlantProcedureRef[]
  schedules: PlantScheduleProfileRef[]
  trigger: PlantLaborTrigger | null
  feedsIn: string[]
  feedsOut: string[]
  /** Exception ops that remediate this op via inbound `RECOVERS`. */
  recoveredBy: PlantLineOperationRef[]
  /** Exception ops that substitute for this op via inbound `FALLBACK_FOR`. */
  fallbacks: PlantLineOperationRef[]
}

export type PlantOperationOrderCoverage = 'full' | 'partial' | 'none'

/** Role chip for HumanLabor staffing. */
export interface PlantRoleRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  role_kind?: string
  description?: string
}

/** ScheduleProfile chip for HumanLabor timing. */
export interface PlantScheduleProfileRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  schedule_type?: string
}

/** Lightweight Tag chip for HumanLabor triggers. */
export interface PlantLaborTagRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  tag_path?: string
}

/** One STAFFED_BY pairing with grade range from the edge. */
export interface PlantLaborStaffing {
  role: PlantRoleRef
  minGrade?: number
  maxGrade?: number
}

/** Optional TRIGGERED_BY pairing with raw condition JSON. */
export interface PlantLaborTrigger {
  tag: PlantLaborTagRef
  conditionJson?: string
}

/** Line-scoped shared capacity covering one or more manual operations. */
export interface PlantCrewRequirementRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  headcount?: number
  description?: string
}

/** One CrewRequirement with its qualification and directly covered operations. */
export interface PlantLineCrewRequirement {
  crew: PlantCrewRequirementRef
  staffing: PlantLaborStaffing[]
  coveredOperations: PlantHumanLaborRef[]
}

/**
 * Legacy HumanLabor station bundle (pre-LineOperation graphs). Same shape
 * as {@link PlantOperationLabor} plus `usedEquipment` from HumanLabor→Equipment
 * `USES_EQUIPMENT`.
 */
export interface PlantLineLaborStation {
  task: PlantHumanLaborRef
  numEmployees?: number
  staffing: PlantLaborStaffing[]
  schedules: PlantScheduleProfileRef[]
  trigger: PlantLaborTrigger | null
  procedures: PlantProcedureRef[]
  usedEquipment: PlantEquipment[]
}

/** Legacy FEEDS/USES surfaces kept for unmigrated HumanLabor graphs. */
export interface PlantLineOperationsLegacy {
  stations: PlantLineLaborStation[]
  flowStations: PlantHumanLaborRef[]
  flowEdges: PlantEdge[]
  /** External Equipment on FEEDS edges that touch this line's stations. */
  flowNeighbors: PlantEquipment[]
}

/**
 * Below-the-fold operations + labor bundle for a Line page
 * (`getPlantLineOperations`). Prefers LineOperation when present; otherwise
 * falls back to the legacy HumanLabor path. Never mixes both into duplicate
 * UI operations — `mode` selects exactly one.
 */
export interface PlantLineOperations {
  mode: 'operations' | 'legacy'
  operations: PlantLineOperation[]
  orderCoverage: PlantOperationOrderCoverage
  crewRequirements: PlantLineCrewRequirement[]
  legacy: PlantLineOperationsLegacy
}

export type PlantTagScopeKind = 'line' | 'process' | 'equipment'

export interface PlantTagScope {
  kind: PlantTagScopeKind
  id: string
}

/**
 * Tags + reachable gateway for a given Line/Process/Equipment node. The proxy
 * walks the graph for tags reachable via `MEASURES`/`REPRESENTS` against
 * equipment in the scope's subtree, and resolves the first `Gateway` upstream
 * via `IS_PROVIDER_FOR -> Site -> Line` so the caller can poll live values
 * without an extra round-trip.
 */
export interface PlantTagsBundle {
  scope: PlantTagScope
  /** Echo of the resolved scope node's display name (for header chips). */
  scopeName: string
  tags: PlantTag[]
  gateway: PlantGatewayRef | null
}

/**
 * Single tag value returned by `POST /api/v1/ontology/live-data/tags`. The proxy
 * delegates to Ignition, so the wire format mirrors the gateway's
 * `read_tag`/`read_tags` shape.
 */
export interface PlantTagValue {
  /** Echo of the requested path. */
  path: string
  /** Raw value (numeric, string, bool, etc.); narrow at render time. */
  value: unknown
  /** Quality string (e.g. "Good", "Bad", "Stale"). */
  quality?: string
  /** ISO-8601 timestamp from the gateway. */
  timestamp?: string
  /** Optional engineering unit, when the gateway returns one. */
  unit?: string
  /** Optional error string for a per-path failure. */
  error?: string
}

export interface PlantTagValuesResponse {
  /**
   * Map of path -> value. The proxy returns `{ "tags": <object> }`; we
   * normalize into an array client-side for predictable rendering.
   */
  tags: PlantTagValue[]
}

export interface PlantLiveTagValuesArgs {
  paths: string[]
  gateway_url: string
  project?: string
}

export interface PlantTagIdValuesArgs {
  node_ids: string[]
}

export interface PlantTagIdValue {
  nodeId: string
  /** Registry tag path when known; empty for unregistered rows. */
  path: string
  value: unknown
  quality?: string
  isGood?: boolean
  timestamp?: string
  source?: string
  stale?: boolean
  errorCode?: string
  warning?: string
  /** True when the registry has no row for this graph node ID. */
  unregistered?: boolean
  /** True when the containing HTTP batch failed before any tag was evaluated. */
  transportError?: boolean
  error?: string
}

export interface PlantTagIdValuesResponse {
  tags: PlantTagIdValue[]
}

/**
 * Plant Viewer "Schedules" tab — one gateway (`:Compute`) row in the
 * landing grid. Returned by the cheap snapshot query that just lists
 * which gateways carry schedules/holidays; the per-gateway live
 * active-status pull happens in `useGetActiveSchedulesQuery`.
 */
export interface PlantScheduleGateway extends PlantEntityMeta {
  /** URN of the `:Compute` node. Stable identity for routing/keys. */
  id: string
  /** Human-readable name (falls back to the URN). */
  name: string
  /** Gateway base URL (`http://host:8088`). Empty when the gateway has not been ingested with a URL. */
  url: string
  /** Optional project name used when the live-tag read needs a project scope. */
  project_name: string | null
  /** Count of `:ScheduleProfile` nodes scoped to this gateway. */
  scheduleProfileCount: number
  /** Count of `:Holiday` nodes scoped to this gateway. */
  holidayCount: number
}

/**
 * One `:ScheduleProfile` row with its live active state, as returned
 * by `POST /api/ontology/schedules/active`.
 */
export interface ActiveScheduleProfile {
  id: string
  name: string
  schedule_type: 'basic schedule' | 'composite schedule'
  description: string
  observe_holidays: boolean
  repeat_mode: string
  sub_schedules: string[]
  active: boolean
  reason: string
}

/**
 * One `:Holiday` row with its live active state.
 */
export interface ActiveHoliday {
  id: string
  name: string
  date: string
  repeat_annually: boolean
  active: boolean
}

/**
 * Response shape from `POST /api/ontology/schedules/active`.
 */
export interface ActiveSchedulesResponse {
  /** Live wall-clock time from the gateway as an ISO string. */
  gateway_time: string
  /** Quality reported by the gateway live-tag read (`Good`, `Bad`, …). */
  gateway_time_quality: string
  /** URN of the `:Compute` matched on `gateway_url`. */
  compute_urn: string | null
  schedule_profiles: ActiveScheduleProfile[]
  holidays: ActiveHoliday[]
}

export interface ActiveSchedulesArgs {
  gateway_url: string
  project_name?: string
}

/**
 * One spatial `Area` in the ontology — a production zone, utility
 * room, cooler, freezer, mezzanine, etc. Drives the Plant Viewer's
 * Areas tab.
 *
 * Surrounding graph (from `tools/ontology-generator/src/nodespec/relationships.json`):
 *   - `Area -[:PART_OF]-> Site`              owning site (or none, for strays)
 *   - `Area -[:PART_OF]-> Area`              parent area (sub-area nesting)
 *   - `Area <-[:PART_OF]- Area`              direct child sub-areas
 *   - `Equipment -[:LOCATED_IN]-> Area`      equipment placed in this area
 *   - `Part -[:LOCATED_IN]-> Area`           parts placed in this area
 *   - `Process -[:LOCATED_IN]-> Area`        processes scoped to this area
 */
export interface PlantArea extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /**
   * `production_zone` | `cooler` | `freezer` | `utility_room` |
   * `machinery_room` | `mezzanine` | `outdoor` | `other`. Optional
   * because partially-populated areas are allowed at ingest.
   */
  area_kind?: string
  /** `ambient` | `refrigerated` | `frozen` | `heated`. */
  temperature_class?: string
  description?: string
}

/**
 * `PlantArea` plus the per-area counts the Areas-tab landing grid
 * needs in one round-trip. Returned by `getPlantAreas` so cards can
 * render badges ("3 sub-areas · 8 equipment") without N+1 follow-ups.
 */
export interface PlantAreaSummary extends PlantArea {
  /** Direct sub-area count (`Area <-[:PART_OF]- Area`). */
  subAreaCount: number
  /**
   * Equipment placed in this area OR any of its descendant sub-areas
   * (`Equipment -[:LOCATED_IN]-> Area <-[:PART_OF*0..]- self`).
   */
  equipmentCount: number
  /**
   * Processes placed in this area or any descendant
   * (`Process -[:LOCATED_IN]-> Area <-[:PART_OF*0..]- self`).
   */
  processCount: number
  /**
   * Parts placed in this area or any descendant
   * (`Part -[:LOCATED_IN]-> Area <-[:PART_OF*0..]- self`).
   */
  partCount: number
  /**
   * Owning site reached via `Area -[:PART_OF*1..]-> Site` (walking up
   * the area-nesting chain). `null` for strays — areas not anchored to
   * any site through the `PART_OF` hierarchy.
   */
  site: PlantSite | null
  /**
   * Immediate parent area when this is a sub-area
   * (`Area -[:PART_OF]-> Area`). `null` for top-level areas (those
   * attached directly to a site, or strays).
   */
  parent: PlantArea | null
}

/**
 * Index bundle for the Areas tab landing — top-level areas grouped
 * by their owning site, plus a separate stray bucket for areas not
 * anchored to any site.
 */
export interface PlantAreaIndex {
  /** Sites that own at least one top-level area, with those areas. */
  groups: Array<{
    site: PlantSite
    areas: PlantAreaSummary[]
  }>
  /**
   * Areas with no `PART_OF` path to a site. Either truly orphaned or
   * `PART_OF`-nested under a parent area that itself isn't on a site.
   */
  strays: PlantAreaSummary[]
}

/** One Part placed in an Area via `Part -[:LOCATED_IN]-> Area`. */
export interface PlantPartRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /** Free-form classification (e.g. `belt`, `bearing`, `sensor`). */
  part_type?: string
  customer_pn?: string
  manufacturer_pn?: string
  is_spare?: boolean
  is_recommended_spare?: boolean
  is_critical_spare?: boolean
}

/** Full Part node for the bespoke Part entity page. */
export interface PlantPartDetailNode extends PlantPartRef {
  vendor?: string
  manufacturer_name?: string
  description?: string
  bin_location?: string
  unit_of_measure?: string
}

/** Equipment or parent Part that contains this Part. */
export interface PlantPartUsedIn {
  owner: PlantGenericEntity
  ownerLabels: string[]
  edge: PlantEdge
}

/** One direct child Part and its `HAS_PART` relationship properties. */
export interface PlantPartChild {
  part: PlantPartRef
  edge: PlantEdge
}

export type PlantVendorRole = 'manufacturer' | 'supplier'

/** Vendor node linked from a Part. */
export interface PlantVendorRef extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  vendor_code?: string
  contact?: string
  vendor_kind?: string
}

/** One Part-to-Vendor relationship, preserving manufacturer vs supplier. */
export interface PlantPartVendor {
  vendor: PlantVendorRef
  role: PlantVendorRole
}

/** Procedure that requires this Part. */
export interface PlantPartProcedure {
  procedure: PlantProcedureRef
  quantity?: number
  isOptional?: boolean
}

/** Area or FunctionalLocation reached through `LOCATED_IN`. */
export interface PlantPartLocation {
  location: PlantGenericEntity
  locationLabels: string[]
}

/** One-shot detail bundle for a Part entity page. */
export interface PlantPartDetail {
  part: PlantPartDetailNode
  usedIn: PlantPartUsedIn[]
  subParts: PlantPartChild[]
  vendors: PlantPartVendor[]
  procedures: PlantPartProcedure[]
  faults: PlantFaultSymptomRef[]
  locations: PlantPartLocation[]
  /** Factory breadcrumb exists only for a directly hosting Equipment. */
  equipment: PlantEquipment | null
  site: PlantSite | null
  line: PlantLine | null
  isStray: boolean
}

/** One Part listed on a Vendor page. */
export interface PlantVendorPart {
  part: PlantPartRef
  role: PlantVendorRole
}

/** One-shot detail bundle for a Vendor entity page. */
export interface PlantVendorDetail {
  vendor: PlantVendorRef
  parts: PlantVendorPart[]
  equipment: PlantEquipment[]
  isStray: boolean
}

/**
 * One-shot detail bundle for a single Area. Powers
 * `/plant/areas/:areaId`: the header reads from `area`, the
 * breadcrumb walks `parents` (root-first) and `site`, child
 * sections render from `subAreas`, `equipment`, `processes`, and
 * `parts`. Each sub-area carries its own counts so the page
 * doesn't need follow-up queries.
 */
export interface PlantAreaDetail {
  area: PlantArea
  /** True when this area matches none of its placement anchor patterns. */
  isStray: boolean
  /** Owning site via the `PART_OF*1..` walk. `null` when stray. */
  site: PlantSite | null
  /**
   * Ancestor areas from root-most down to the immediate parent
   * (top-of-list is the area closest to the site). Empty when this
   * area is itself top-level (attached directly to a site).
   */
  parents: PlantArea[]
  /** Direct sub-areas (`Area <-[:PART_OF]- Area`), with summary counts. */
  subAreas: PlantAreaSummary[]
  /** Equipment placed in this area only (not in nested sub-areas). */
  equipment: PlantEquipment[]
  /** Processes placed in this area only (not in nested sub-areas). */
  processes: PlantProcess[]
  /** Parts placed in this area only (not in nested sub-areas). */
  parts: PlantPartRef[]
  /** Conditions exhibited directly by this area. */
  faults: PlantFaultSymptomRef[]
}

/**
 * A single compute host in the ontology. Replaces the legacy `Gateway`
 * label per `axilon-apps/docs/scada-ontology-spec.md` §2. One Compute
 * per distinct host (Ignition gateway, AVEVA galaxy, OPC server, DB
 * server, …). Drives the Plant Viewer's Compute tab.
 */
export interface PlantComputeHost extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /**
   * `ignition` | `aveva` | `opc` | `db_server` | `device` | `other`
   * per spec §2. Optional because partially-populated hosts are
   * allowed at ingest. Drives the icon + label on host cards.
   */
  compute_type?: string
  host?: string
  url?: string
  version?: string
  vendor?: string
  /** Decoded Experion EMDB enum label when this Compute came from EMDB. */
  kind?: string
  opc_endpoint?: string
  opc_root_node_id?: string
  opc_security_mode?: string
  source_file?: string
  /**
   * Per-Compute Ignition WebDev project, e.g. `Axilon` (the in-code
   * fallback) or `global/tagData`. Set by structured ingest and read by
   * the proxy's `WebDevProjectResolver` for live-data / database URL
   * construction. `undefined` on non-Ignition hosts and on
   * Ignition hosts ingested before this property existed.
   */
  web_dev_project?: string
  /**
   * Ignition device driver kind for `compute_type: 'device'` rows
   * (e.g. `'ModbusTcp'`, `'LogixDriver'`). Written by the
   * `device-mapper`; absent on every other compute type. Surfaced
   * as a chip on the Device cards in the Compute tab so the user
   * can tell PLC kinds apart without drilling in.
   */
  device_driver?: string
}

/**
 * A logical tag provider hosted by a `Compute` (e.g. an Ignition tag
 * provider, an AVEVA `aveva` provider, or a remote OPC UA provider).
 * Reached via `Compute -[:HAS_TAG_PROVIDER]-> TagProvider`. `tagCount`
 * is the count of direct `(:TagProvider)-[:PROVIDES]->(:Tag)` rows
 * only — UDT-member leaves are intentionally not included because
 * counting them eagerly across every provider on a host is
 * prohibitively expensive on real Ignition gateways. The
 * provider-browser totals (filled by `getPlantProviderTags`)
 * include UDT-member leaves. Filled by `getPlantComputeDetail`;
 * zero when no direct tags are reachable.
 */
export interface PlantTagProvider extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /** `ignition` | `aveva` | `opc-ua` | `opc-da` | `mqtt` | `remote` per spec §2. */
  protocol?: string
  url?: string
  enabled?: boolean
  /** Set on `remote`-protocol providers — points at another Compute's URL. */
  remote_compute_url?: string
  /** Set on `remote`-protocol providers — names the upstream provider. */
  remote_provider_name?: string
  tagCount: number
}

/**
 * A database catalog hosted on a `Compute`. Replaces the legacy
 * `DatabaseConnection` label per spec §3; carries connection metadata
 * directly. Reached via direct `Compute -[:HAS_DATABASE]-> Database`,
 * or on an Ignition gateway via
 * `Compute -[:NETWORKS_WITH]-> (:Compute) -[:HAS_DATABASE]-> Database`
 * (ingest hangs JDBC catalogs on a `db_server` Compute). Tabular-dump
 * ingest MERGEs a standalone `:Database` (`urn:db:<name>`,
 * `database_type: 'DUCKDB'`) with no Compute; Plant View projects those
 * as `db_server` hosts so they sit with the other catalogs.
 * `tableCount` walks `Database -[:CONTAINS_TABLE]-> Table` and is
 * filled by `getPlantComputeDetail`.
 */
export interface PlantDatabase extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  /** `MYSQL` | `MSSQL` | `POSTGRESQL` per spec §3. */
  database_type?: string
  url?: string
  enabled?: boolean
  validation_query?: string
  tableCount: number
}

// ─── Queries (NamedQuery / query-flavored Tag) ────────────

/**
 * Discriminates the two database-query flavors the Queries tab
 * surfaces. `:QueryTag` is a nodespec label that is never MERGEd at
 * runtime — a query tag is always either a `:Tag` with
 * `ignition_tag_type = 'query'` or `tag_system = 'db'` — so
 * `query_tag` covers both ingested Ignition query tags and manually
 * registered `db://` data points.
 */
export type PlantQueryKind = 'named_query' | 'query_tag'

/** Lightweight `:Database` reference reached via `USES_DATABASE`. */
export interface PlantDatabaseRef {
  id: string
  name: string
  database_type?: string
  url?: string
}

/** Lightweight `:Table` reference reached via `USES_TABLE`. */
export interface PlantQueryTableRef {
  id: string
  name: string
  schema?: string
}

/** A single query parameter parsed off a `:NamedQuery` node. */
export interface PlantQueryParameter {
  name: string
  type?: string
}

/**
 * The `:Compute` host owning a query's database. Carries the gateway
 * `url` (+ `compute_type`) so a "register as data point" action can
 * rebuild the exact connection posture the query reads through — more
 * than the chip-only {@link PlantComputeRef}.
 */
export interface PlantQueryHostRef {
  id: string
  name: string
  compute_type?: string
  url?: string
}

/**
 * Raw `:NamedQuery` or query-flavored `:Tag` node as projected from
 * cypher. Carries the union of both flavors' properties (the SQL text
 * lives under `query_text` on NamedQuery and `query` on a query Tag;
 * the datasource under `db_name` vs `datasource`), so the extractors
 * normalize before the UI reads them.
 */
export interface PlantQueryNode extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  // NamedQuery flavor
  query_name?: string
  project_name?: string
  db_name?: string
  query_type?: string
  syntax?: string
  folder_path?: string
  parameter_count?: number
  query_text?: string
  columns?: unknown
  parameters?: unknown
  // query-Tag flavor
  tag_path?: string
  tag_system?: string
  ignition_tag_type?: string
  datasource?: string
  query?: string
  source_kind?: string
}

/** One row on the Queries tab landing. */
export interface PlantQuerySummary extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  kind: PlantQueryKind
  /** Normalized SQL text (NamedQuery `query_text` or Tag `query`). */
  queryText?: string
  /** Datasource / database display name. */
  databaseName?: string
  database?: PlantDatabaseRef
  tableCount: number
  projectName?: string
  queryType?: string
  /**
   * Leaf query name (NamedQuery `query_name`) — the display label inside a
   * folder tree, stripped of the `project/folder` prefix carried by `name`.
   */
  queryName?: string
  /**
   * Slash-separated folder path a NamedQuery lives under within its project
   * (Ignition `folder_path`). Empty/undefined means the project root.
   */
  folderPath?: string
}

/**
 * One node in the named-query folder tree: a project root or an intermediate
 * folder. `queries` holds the leaf queries living directly in this folder;
 * `folders` are the nested subfolders. Built by `buildQueryFolderTree`.
 */
export interface PlantQueryFolderNode {
  /** Folder segment display name (project name at the tree root). */
  name: string
  /** Full slash-joined key (`project/folder/subfolder`) — stable React key. */
  path: string
  folders: PlantQueryFolderNode[]
  queries: PlantQuerySummary[]
}

/** Detail bundle for a single query (either flavor). */
export interface PlantQueryDetail {
  query: PlantQueryNode
  kind: PlantQueryKind
  /** Normalized SQL text (NamedQuery `query_text` or Tag `query`). */
  queryText?: string
  /** Datasource / database display name. */
  databaseName?: string
  database?: PlantDatabaseRef
  /** Owning `:Compute` host — the gateway a register-as-data-point reads through. */
  host?: PlantQueryHostRef
  tables: PlantQueryTableRef[]
  /** Column names parsed off a NamedQuery node (empty for query tags). */
  columns: string[]
  parameters: PlantQueryParameter[]
}

// ─── Protective functions (SRS Safety tab) ────────────────

/**
 * A `:ProtectiveFunction` node as projected from cypher. Carries the
 * SRS-declared identity and SIL/demand fields the Safety tab lists
 * and the detail page renders as specifications.
 */
export interface PlantProtectiveFunction extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  function_type?: string
  description?: string
  ipl_description?: string
  safe_state?: string
  demand_mode?: string
  sil?: string
  required_sil?: string
  required_rrf?: string
  sil_pfd_avg?: string
  sil_verification_snapshot?: string
  sil_verification_source?: string
  pfd_avg?: string
  rrf?: string
  response_time?: string
  process_safety_time?: string
  bypass?: string
  reset?: string
  alarm?: string
  logic_solver?: string
  system_id?: string
  scenario?: string
  purpose?: string
  service?: string
}

/** The SRS chapter that classifies a protective function. */
export interface PlantSrsSection extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  header?: string
  function_group?: string
  function_category?: string
  package_id?: string
  package_title?: string
  docs_path?: string
  docs_anchor?: string
}

/** One site-scoped SRS function group assembled from related sections. */
export interface PlantSrsFunctionGroup {
  /** Stable route anchor: the lexicographically first section id in this group. */
  id: string
  name: string
  site?: PlantSite
  sections: PlantSrsSection[]
  functions: PlantProtectiveFunctionSummary[]
}

/** Compact Safety landing model plus functions without section metadata. */
export interface PlantSrsFunctionGroupIndex {
  groups: PlantSrsFunctionGroup[]
  ungrouped: PlantProtectiveFunctionSummary[]
}

/** One row on the Safety tab landing. */
export interface PlantProtectiveFunctionSummary extends PlantProtectiveFunction {
  line?: PlantLine
  site?: PlantSite
  solver?: PlantLogicSolver
  section?: PlantSrsSection
  sensorCount: number
  finalElementCount: number
  implementedDiagrams: PlantFunctionLogicDiagram[]
}

/** A `:LogicSolver` node as projected from cypher (Safety Manager, C300, etc.). */
export interface PlantLogicSolver extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  type?: string
  vendor?: string
  application?: string
}

/** A `:FunctionLogicDiagram` node as projected from cypher. */
export interface PlantFunctionLogicDiagram extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  fld_number?: string
  description?: string
  application?: string
}

/** A `:SafetyPoint` node as projected from cypher. */
export interface PlantSafetyPoint extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  point_type?: string
  description?: string
  safety_related?: boolean
  tag_number?: string
  location?: string
  unit?: string
  subunit?: string
  sil?: string
  fld_number?: string
  application_address?: string
  block_name?: string
  chassis_id?: string
  slot_number?: string
  channel_number?: string
  data_type?: string
  signal_type?: string
  engineering_units?: string
  soe_enable?: boolean
  force_enable?: boolean
  write_enable?: boolean
  is_internal?: boolean
}

/** One FLD row on the Safety landing or a solver detail. */
export interface PlantFunctionLogicDiagramSummary extends PlantFunctionLogicDiagram {
  solver?: PlantLogicSolver
  site?: PlantSite
  pointCount: number
  implementedBy: PlantProtectiveFunction[]
}

/** Solver bucket on the Safety landing. */
export interface PlantProtectiveFunctionSolverGroup {
  solver: PlantLogicSolver
  functions: PlantProtectiveFunctionSummary[]
  diagrams: PlantFunctionLogicDiagramSummary[]
}

/** Safety tab landing: logic-solver groups plus functions with no solver. */
export interface PlantProtectiveFunctionIndex {
  solvers: PlantProtectiveFunctionSolverGroup[]
  unsolved: PlantProtectiveFunctionSummary[]
  unsolvedDiagrams: PlantFunctionLogicDiagramSummary[]
}

/** Detail bundle for a single `:LogicSolver`. */
export interface PlantLogicSolverDetail {
  solver: PlantLogicSolver
  site?: PlantSite
  diagrams: PlantFunctionLogicDiagramSummary[]
  functions: PlantProtectiveFunction[]
}

/** Detail bundle for a single `:FunctionLogicDiagram`. */
export interface PlantFunctionLogicDiagramDetail {
  diagram: PlantFunctionLogicDiagram
  solver?: PlantLogicSolver
  site?: PlantSite
  points: PlantSafetyPoint[]
  functions: PlantProtectiveFunction[]
}

/** Detail bundle for a single `:SafetyPoint`. */
export interface PlantSafetyPointDetail {
  point: PlantSafetyPoint
  diagram?: PlantFunctionLogicDiagram
  solver?: PlantLogicSolver
  site?: PlantSite
  functions: PlantProtectiveFunction[]
  equipment: PlantEquipment[]
}

/** Equipment → Safety Manager point reverse lookup. */
export interface PlantEquipmentSafetyPointLink {
  point: PlantSafetyPoint
  diagram?: PlantFunctionLogicDiagram
  solver?: PlantLogicSolver
  functions: PlantProtectiveFunction[]
}

/**
 * An Equipment reached from a ProtectiveFunction via `SENSED_BY` or
 * `ACTUATES`, plus the group/voting properties that live on the edge.
 */
export interface PlantProtectiveFunctionDevice {
  equipment: PlantEquipment
  group?: string
  voting?: string
  voting_type?: string
  fail_direction?: string
  trip_setpoint?: string
  range?: string
  sensor_type?: string
  action?: string
  stroke?: string
  fail_design?: string
  test_interval?: string
}

/** Detail bundle for a single `:ProtectiveFunction`. */
export interface PlantProtectiveFunctionDetail {
  function: PlantProtectiveFunction
  line?: PlantLine
  site?: PlantSite
  solver?: PlantLogicSolver
  section?: PlantSrsSection
  sensors: PlantProtectiveFunctionDevice[]
  finalElements: PlantProtectiveFunctionDevice[]
  protectedEquipment: PlantEquipment[]
  implementedDiagrams: PlantFunctionLogicDiagram[]
  implementedPoints: PlantSafetyPoint[]
}

/**
 * How an Equipment participates in a ProtectiveFunction. Mirrors the
 * reverse of `SENSED_BY` / `ACTUATES` / `PROTECTS`.
 */
export type PlantProtectiveFunctionLinkRole = 'sensor' | 'final_element' | 'protected'

/** One Safety-tab function linked from an equipment detail page. */
export interface PlantEquipmentProtectiveFunctionLink {
  function: PlantProtectiveFunction
  roles: PlantProtectiveFunctionLinkRole[]
}

/**
 * Lightweight reference to another `Compute` node that connects to
 * this host over a `NETWORKS_WITH` edge. Carries just enough to
 * render a chip and follow a link — full host details land via a
 * follow-up `getPlantComputeDetail` when the user navigates in.
 */
export interface PlantComputeRef {
  id: string
  name: string
  compute_type?: string
}

/**
 * Lightweight reference to a `Controller` node reached via
 * `(:Compute)-[:MAPS_TO_CONTROLLER]->(:Controller)`. Carries just
 * enough to render a chip and follow a link into the PLC tab.
 */
export interface PlantControllerRef {
  id: string
  name: string
  type?: string
}

/**
 * An Equipment or Line that is `MONITORED_BY` a Compute — the inverse of
 * the asset detail pages' compute chip. Carries just enough to render a
 * chip and deep-link back into the Equipment / Line tab. The `id` is a
 * site-scoped URN, so `plantHref` can rebuild the route without a separate
 * site lookup.
 */
export interface PlantMonitoredAsset {
  id: string
  name: string
  /** Discriminates the deep-link target + icon. */
  kind: 'equipment' | 'line'
  /** `equipment_type` for equipment, `line_code` for lines (when present). */
  type?: string
}

/**
 * `PlantComputeHost` plus the per-host counts the Compute-tab landing
 * grid needs in one round-trip. Returned by `getPlantComputeIndex` so
 * cards can render badges ("3 providers · 2 databases") without an
 * N+1 follow-up.
 *
 * `gateways` carries the inbound `NETWORKS_WITH` neighbours — the
 * Ignition / AVEVA gateways that talk to this host. Always present
 * on `compute_type: 'device'` rows (so the Devices section can show
 * which gateway each PLC is wired to) and present on any other
 * Compute that has an inbound `NETWORKS_WITH` edge (e.g. an OPC
 * server or DB server attached to a gateway). Empty when the host
 * is a standalone Compute (no inbound edge).
 */
export interface PlantComputeHostSummary extends PlantComputeHost {
  /** Direct count of `Compute -[:HAS_TAG_PROVIDER]-> TagProvider`. */
  tagProviderCount: number
  /**
   * Count of Databases on this host: direct `HAS_DATABASE` plus, for
   * gateways, catalogs on outbound `NETWORKS_WITH` `db_server` hosts.
   */
  databaseCount: number
  /** Inbound `(:Compute)-[:NETWORKS_WITH]->(this)` neighbours, name-sorted. */
  gateways: PlantComputeRef[]
  /**
   * Outbound `(:Compute)-[:MAPS_TO_CONTROLLER]->(:Controller)` targets.
   * Populated on `compute_type: 'opc' | 'device'` rows after IP-mapping
   * ingest; empty on every other host type.
   */
  controllers: PlantControllerRef[]
  /**
   * Outbound `(:Compute)-[:MAPS_TO_CONTROLLER]->(:DcsController)` targets.
   * Populated after Experion ERDB ingest; empty otherwise.
   */
  dcsControllers: PlantControllerRef[]
}

/**
 * One-shot detail bundle for a single Compute host. Powers
 * `/plant/compute/hosts/:computeId`: the host card header reads from
 * `host`, the providers grid from `tagProviders`, the databases grid
 * from `databases`. Each provider / database already carries its own
 * child count so the page doesn't need follow-up queries.
 */
export interface PlantComputeHostDetail {
  host: PlantComputeHost
  /** True when this compute matches none of its placement anchor patterns. */
  isStray: boolean
  tagProviders: PlantTagProvider[]
  databases: PlantDatabase[]
  /**
   * Inbound `(:Compute)-[:NETWORKS_WITH]->(host)` neighbours — the
   * gateways that talk to this host. Used by the host detail page to
   * render "Part of {gateway name}" links so a device / OPC server /
   * DB server makes its parent Ignition gateway discoverable from a
   * deep link.
   */
  gateways: PlantComputeRef[]
  /**
   * Outbound `(:Compute)-[:MAPS_TO_CONTROLLER]->(:Controller)` targets.
   * Used by the host detail page to render links into the PLC tab for
   * `compute_type: 'opc' | 'device'` hosts.
   */
  controllers: PlantControllerRef[]
  /**
   * Outbound `(:Compute)-[:MAPS_TO_CONTROLLER]->(:DcsController)` targets.
   * Used by the host detail page to render links into the DCS tab.
   */
  dcsControllers: PlantControllerRef[]
  /**
   * Inbound `(Equipment|Line)-[:MONITORED_BY]->(host)` assets — the plant
   * gear whose telemetry this compute hosts. Lets the host page link back
   * into the Equipment / Line tabs (the inverse of the asset pages'
   * compute chip).
   */
  monitoredAssets: PlantMonitoredAsset[]
}

/**
 * One `:Tag` reachable from a `TagProvider` via `:PROVIDES`. Surfaced
 * by the tag-provider browser panel on a compute-host detail page.
 * Live values are intentionally absent here — the polling layer
 * handles them; this is the static identity + metadata.
 *
 * Synthesised UDT-member leaves coming from
 * `(UDTInstance)-[:INSTANCE_OF]->(UDTDefinition)-[:HAS_MEMBER]->(UDTMember)`
 * (and instance-level overrides via `UDTInstance-[:HAS_MEMBER]->UDTMember`)
 * also land in this shape so the flat-list table can render them
 * uniformly. They carry `ignition_tag_type: 'UDTMember'` so the UI can
 * show a small "UDT" pill and so `tagPollPath` can still construct a
 * valid `[provider]<udt-path>/<member-name>` for live polling.
 */
export interface PlantProviderTag {
  id: string
  _id: string
  name: string
  /**
   * Canonical Ignition tag path (`[Provider]Folder/Tag`). Carried on
   * the `:Tag` node post-UDT-ingest-refactor — both regular tags and
   * UDT-instance member tags now emit it at ingest so consumers can
   * pass it straight to `/live-data/tags` without recomposing
   * `[provider]name` (which doesn't work for UDT-instance members
   * whose `name` is the within-instance path only). Optional in the
   * DTO so malformed wire rows can be represented and skipped safely.
   */
  tag_path?: string
  /**
   * Projected Ignition tag kind. Direct rows carry their graph
   * `ignition_tag_type` (or `'Tag'` when absent); UDT leaves carry the
   * synthetic `'UDTMember'` value so the UI can paint its UDT pill.
   * See `member_type` for a UDT member's actual value-source kind.
   */
  ignition_tag_type?: string
  data_type?: string
  /**
   * Value-source kind for UDT leaf members projected from the graph
   * (`opc` / `memory` / `expression` / `reference` / `derived` /
   * `query` / `atomic`). Read directly from `UDTMember.member_type`
   * by the plant browser queries. Populated only for UDT-member
   * synthesised rows; falls back to a `'Data'` column label when
   * `data_type` is missing (the case for backups ingested before
   * the udt-mapper started persisting `data_type`).
   */
  member_type?: string
  folder?: string
  /** Physical OPC metadata projected for inspection, never live-read identity. */
  opc_server?: string
  opc_item_path?: string
  opc_node_id?: string
  eng_unit?: string
  eng_low?: number
  eng_high?: number
  read_only?: boolean
  history_enabled?: boolean
}

export interface PlantProviderTagsArgs {
  providerId: string
  /**
   * Server-side substring match on `name` + `folder`, case-insensitive.
   * `undefined` returns every tag the provider hosts.
   */
  search?: string
  /** Zero-indexed page. Server computes `SKIP = page * pageSize`. */
  page: number
  /** Defaults to 100 server-side; overridable per call site. */
  pageSize?: number
}

/**
 * One page returned by `getPlantProviderTags`. `totalTags` reflects the
 * search filter (so the panel header can show "37 tags match \"pump\"")
 * and is stable across pages — clients derive `totalPages` from it.
 */
export interface PlantProviderTagsPage {
  totalTags: number
  tags: PlantProviderTag[]
}

/**
 * Direct subfolder of a `TagProvider` at a given path. `tagCount` is
 * the recursive count of every tag-like leaf below this subfolder
 * (both `:Tag` nodes and `:UDTMember` leaves reachable through
 * `:UDTInstance`s rooted in that subtree) — matches the "X tags in
 * this folder" indicator file explorers conventionally show.
 */
export interface PlantFolderSubfolderEntry {
  name: string
  tagCount: number
}

/**
 * A `:UDTInstance` rooted directly under the current tag-folder path
 * (`UDTInstance.provider_name = <provider>` AND
 * `coalesce(UDTInstance.folder_name, '') = $currentPath`). Rendered
 * inline in the explorer as a folder row alongside plain tag-folder
 * subfolders. `definitionId` lets the client drill straight into the
 * member tree without re-resolving the `INSTANCE_OF` edge.
 */
export interface PlantProviderUdtInstanceEntry {
  id: string
  /** Last path segment of the instance, e.g. `SWAGER1`. */
  instanceName: string
  /** Graph id of the connected `:UDTDefinition`, or null if missing. */
  definitionId: string | null
  /** Display label for the definition (`UDTDefinition.name`). */
  definitionName: string | null
  /** Recursive count of leaf `:UDTMember`s under this instance. */
  memberCount: number
}

export interface PlantProviderFolderNodeArgs {
  providerId: string
  /** Slash-joined path inside the provider; empty string = root. */
  path: string
}

/**
 * One level of the tag-provider folder tree. Returned by
 * `getPlantProviderFolderNode`; powers the file-explorer view of the
 * tag-provider browser panel. Each navigation step (drill in, breadcrumb
 * jump, back) re-fires the query with a new `path`; RTK Query caches
 * by `(providerId, path)` so revisits are free.
 */
export interface PlantProviderFolderNodeResponse {
  /** Tags whose `folder` equals exactly the requested path. */
  directTags: PlantProviderTag[]
  /** First-segment subfolder names + recursive tag counts below each. */
  subfolders: PlantFolderSubfolderEntry[]
  /**
   * `:UDTInstance`s anchored at exactly this tag-folder path. Rendered
   * as folder rows in the explorer; clicking one transitions the
   * navigation into UDT mode (see `getPlantUdtFolderNode`).
   */
  directUdtInstances: PlantProviderUdtInstanceEntry[]
}

export interface PlantUdtFolderNodeArgs {
  /** Graph id of the `:UDTInstance` we're navigating inside of. */
  udtInstanceId: string
  /**
   * Graph id of the current `:UDTDefinition` whose member tree we're
   * walking. Starts as the `INSTANCE_OF` definition of the root
   * instance; switches to a nested-member's definition each time the
   * user drills into a `member_type='udt_instance'` member.
   */
  definitionId: string
  /**
   * Slash-joined member-name prefix inside the current definition.
   * Empty string = root of the definition's member tree.
   */
  subPath: string
  /**
   * Provider name (used to construct pollable
   * `[provider]<udt-path>/<member-name>` strings on the server when
   * synthesising leaf-member tag rows).
   */
  providerName?: string
  /**
   * Full UDT instance path (e.g. `Line1/Motor_01`). Used as the prefix
   * for synthesised leaf-member tag-row names so client-side polling
   * and search work unchanged.
   */
  instanceFullPath?: string
}

/**
 * One nested `:UDTInstance` HAS_MEMBER from the current `:UDTInstance`.
 * Rendered as a folder row in the explorer; clicking one pushes a
 * `UdtNestedInstanceSegment` onto the path stack carrying
 * `nestedInstanceId`. The next `getPlantUdtFolderNode` call then
 * uses that id as `$udtInstanceId`, so its `HAS_MEMBER` walk lands
 * directly on the nested instance's own `:Tag` children (which carry
 * the canonical Ignition `tag_path` for live polling).
 */
export interface PlantUdtNestedInstanceEntry {
  /**
   * Graph id of the runtime nested `:UDTInstance` node — used both
   * as a React key and as the next `$udtInstanceId` when the user
   * drills in. Was the `:UDTMember` template id pre-refactor; is now
   * always the runtime instance id since the explorer navigates the
   * instance-side tree directly.
   */
  id: string
  /**
   * Convenience alias of `id` named after its semantic role —
   * "the URN to send as $udtInstanceId after I navigate into this
   * nested instance". Same value as `id`; kept as a separate field
   * so callers reading the path-segment state don't have to know
   * the React-key/navigation-target conflation.
   */
  nestedInstanceId: string
  /** Leaf segment of the member name (last path component). */
  memberName: string
  /** `UDTDefinition.name` the nested instance is typed as. */
  nestedDefinitionName: string | null
  /** Graph id of the resolved nested `:UDTDefinition`, or null. */
  nestedDefinitionId: string | null
  /** Count of direct leaf `:Tag` children under this nested instance. */
  memberCount: number
}

/**
 * One level of a UDT instance's member tree. Mirrors
 * `PlantProviderFolderNodeResponse` but for in-UDT navigation:
 *   - `directMembers` are the leaf `:UDTMember` rows at exactly
 *     `subPath`; rendered as tag rows and live-polled.
 *   - `directNestedInstances` are `member_type='udt_instance'` members
 *     at exactly `subPath`; rendered as folder rows.
 *   - `subfolders` are first-segment prefixes of member names strictly
 *     below `subPath` (in-definition subfolders).
 */
export interface PlantUdtFolderNodeResponse {
  directMembers: PlantProviderTag[]
  directNestedInstances: PlantUdtNestedInstanceEntry[]
  subfolders: PlantFolderSubfolderEntry[]
}

/**
 * One column of a database table as reported by Ignition WebDev's
 * `describeDbSchema` endpoint. The shape varies across DB drivers —
 * some setups return only column names; others include type and
 * nullability. Both forms are normalized into this shape at the
 * extraction layer.
 */
export interface PlantDbColumn {
  name: string
  type?: string
  nullable?: boolean
}

export interface PlantDbTable {
  name: string
  /** Optional schema namespace (Postgres / SQL Server). MySQL omits. */
  schema?: string
  columns: PlantDbColumn[]
}

/** Postgres is the only direct-mode target supported today. */
export type PlantDbDirectDatabaseType = 'POSTGRESQL'

/**
 * Args for `GET /api/v1/ontology/db/connections/{id}/schema`.
 * `connectionId` is the graph `:Database` URN (wire `connection_id`).
 */
export interface PlantDbSchemaArgs {
  connectionId: string
  /** Optional schema filter; when set, only tables in that schema return. */
  schema?: string
}

export interface PlantDbSchemaResponse {
  database: string
  schema: string | null
  tables: PlantDbTable[]
  tableCount: number
}

export type PlantDbQueryType = 'read' | 'write'

/**
 * Args for `POST /api/v1/ontology/db/connections/{id}/query`.
 * Read vs write is selected by `query_type` on the request body
 * (`executePlantDbReadQuery` / `executePlantDbWriteQuery`), not by URL.
 */
export interface PlantDbExecuteArgs {
  connectionId: string
  query: string
  /** Positional prepared-statement args, if any. */
  args?: unknown[]
}

/**
 * Result of a Plant DB query. Read vs. write is decided by `query_type`
 * on `POST …/db/connections/{id}/query` (`executePlantDbReadQuery` vs
 * `executePlantDbWriteQuery`). The shape of `result` varies:
 *  - read  : `Array<Record<string, unknown>>` — array of row dicts,
 *            keyed by column name; column order = first row's keys.
 *  - write : `number` — the update count from `runPrepUpdate`.
 *
 * Kept as `unknown` here because narrowing only matters at render
 * time, where `parseDbResult` in `DatabaseQueryEngine.tsx` projects
 * either shape into a `{ kind, ... }` tagged union for the results
 * table.
 */
export interface PlantDbExecuteResult {
  result: unknown
}

// ─── Robotics tab (Plant Viewer) ────────────────────────────
//
// Motoman/Yaskawa robot-controller backups materialize controller,
// arm, job, I/O signal, and condition nodes. The Plant Viewer exposes
// Controller -> Arm -> Job as the primary tree and lets detail panes link
// sideways to signals, conditions, call graph neighbours, and controlled
// robotic-cell equipment.

export type PlantRobotNodeKind =
  | 'RobotController'
  | 'RobotArm'
  | 'RobotJob'
  | 'RobotIOSignal'
  | 'RobotCondition'
  | 'VisionProcess'
  | 'VisionData'

export interface PlantRobotController extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  vendor?: string
  model?: string
  controller_series?: string
  application?: string
  software_version?: string
  ip_address?: string
  metadata_method?: string
  needs_review?: boolean
}

export interface PlantRobotControllerRef {
  id: string
  name: string
  display_name?: string
  vendor?: string
  model?: string
  needs_review?: boolean
}

export interface PlantRobotArm extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  group?: string
  model?: string
  axis_count?: number
  payload_kg?: number
  assignment_method?: string
  needs_review?: boolean
}

export interface PlantRobotJob extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  job_type?: string
  group?: string
  comment?: string
  is_master?: boolean
  instruction_count?: number
  position_count?: number
  source_code?: string
  master_job_method?: string
  assignment_method?: string
  needs_review?: boolean
}

export interface PlantRobotIOSignal extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  address?: string
  direction?: 'IN' | 'OUT'
  signal_number?: number
  comment?: string
  /**
   * Legacy node property. Prefer {@link plcTags} from
   * `MAPS_TO_ROBOT_SIGNAL` edges — ingest does not stamp `plc_tag`.
   */
  plc_tag?: string
  /** PLC tags linked via inbound `MAPS_TO_ROBOT_SIGNAL`. */
  plcTags?: PlantPLCTag[]
  mapping_method?: string
  needs_review?: boolean
}

export interface PlantRobotCondition extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  scheme?: string
  index?: number
  condition_type?: string
  comment?: string
}

/** A robot vision process (FANUC iRVision) — see the `VisionProcess` nodespec. */
export interface PlantVisionProcess extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  vendor?: string
  vision_system?: string
  /** Process type an XML vision data file declares, e.g. `Error Proofing`. */
  type_label?: string
  /** Vision tools the XML vision data file configures. */
  tools?: string[]
  comment?: string
  source_format?: string
  applications?: string[]
  result_kinds?: string[]
  operations?: string[]
  vision_registers?: number[]
  camera_views?: number[]
  offset_applied?: boolean
  remote_controller?: string
  has_vision_data_file?: boolean
  vision_data_size_bytes?: number
  source_file?: string
  needs_review?: boolean
}

/** Non-process vision data (camera setup / calibration / override / unknown). */
export interface PlantVisionData extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  vision_data_type?: string
  /** Type an XML vision data file declares, e.g. `Grid Pattern Calibration`. */
  type_label?: string
  comment?: string
  source_format?: string
  vendor?: string
  vision_system?: string
  operations?: string[]
  has_vision_data_file?: boolean
  vision_data_size_bytes?: number
  source_file?: string
  needs_review?: boolean
}

/**
 * One vision-process ↔ I/O-signal link (`OUTPUTS_TO` / `TRIGGERS`), with the
 * PLC tags mapped to that signal and the SCADA tags reachable from it.
 */
export interface PlantVisionSignalLink {
  signal: PlantRobotIOSignal
  resultKinds: string[]
  /** `value` / `conditional` (job code), `wait` (trigger), or `vision_data` (named in the vision data file). */
  via: string[]
  jobs: string[]
  /** Vision data fields naming the signal, when `via` includes `vision_data`. */
  dataFields: string[]
  method?: string
  plcTags: PlantPLCTag[]
  scadaTags: PlantPlcScadaTag[]
}

/** A vision process as seen from one of its I/O signals. */
export interface PlantVisionProcessLink {
  process: PlantVisionProcess
  resultKinds: string[]
  via: string[]
  jobs: string[]
  dataFields: string[]
}

/** A `USES_VISION_DATA` peer with its best-effort edge metadata. */
export interface PlantVisionDataRef<T> {
  node: T
  method?: string
  confidence?: number
  needsReview: boolean
}

export type PlantRobotTreeNodeKind = Extract<
  PlantRobotNodeKind,
  'RobotController' | 'RobotArm' | 'RobotJob'
>

export interface PlantRobotTreeNode extends PlantEntityMeta {
  id: string
  parentId: string | null
  kind: PlantRobotTreeNodeKind
  name: string
  armCount: number
  jobCount: number
  signalCount: number
  conditionCount: number
  needsReview: boolean
}

export interface PlantRoboticsTree {
  nodes: PlantRobotTreeNode[]
  totalControllerCount: number
  totalArmCount: number
  totalJobCount: number
}

/**
 * One relationship site with optional ladder/instruction locus props.
 * Used for CALLS_JOB / READ_BY / WRITTEN_BY / USED_BY so repeated edges
 * to the same peer remain distinct.
 */
export interface PlantRobotRelationSite<T> {
  node: T
  instruction?: string
  line?: number
  opcode?: string
}

/** One peer with all of its instruction sites collapsed under it. */
export interface PlantRobotRelationGroup<T> {
  node: T
  sites: Array<{ instruction?: string; line?: number; opcode?: string }>
}

/** Outbound / inbound SUPERVISES row with edge props. */
export interface PlantRobotSupervisionRef {
  controller: PlantRobotController
  /** `master` | `slave` | `line_tracking`. */
  role?: string
  networkPath?: string
}

/**
 * Inverse `MAPS_TO_ROBOT_SIGNAL` row from a PLCTag onto this robot signal.
 */
export interface PlantRobotPlcTagMapping {
  tag: PlantPLCTag
  method?: string
  confidence?: number
  register?: string
  direction?: string
}

export interface PlantRobotControllerDetail {
  kind: 'RobotController'
  isStray: boolean
  controller: PlantRobotController
  arms: PlantRobotArm[]
  jobs: PlantRobotJob[]
  signals: PlantRobotIOSignal[]
  conditions: PlantRobotCondition[]
  controlledEquipment: PlantEquipment[]
  visionProcesses: PlantVisionProcess[]
  visionData: PlantVisionData[]
  /** Outbound `SUPERVISES` subordinates. */
  supervises: PlantRobotSupervisionRef[]
  /** Inbound `SUPERVISES` masters. */
  supervisedBy: PlantRobotSupervisionRef[]
}

export interface PlantRobotArmDetail {
  kind: 'RobotArm'
  isStray: boolean
  arm: PlantRobotArm
  controller: PlantRobotController | null
  jobs: PlantRobotJob[]
  signals: PlantRobotIOSignal[]
  conditions: PlantRobotCondition[]
}

export interface PlantRobotJobDetail {
  kind: 'RobotJob'
  isStray: boolean
  job: PlantRobotJob
  arm: PlantRobotArm | null
  controller: PlantRobotController | null
  calls: PlantRobotRelationSite<PlantRobotJob>[]
  calledBy: PlantRobotRelationSite<PlantRobotJob>[]
  readSignals: PlantRobotRelationSite<PlantRobotIOSignal>[]
  writtenSignals: PlantRobotRelationSite<PlantRobotIOSignal>[]
  conditions: PlantRobotRelationSite<PlantRobotCondition>[]
  visionProcesses: PlantRobotRelationSite<PlantVisionProcess>[]
  visionData: PlantRobotRelationSite<PlantVisionData>[]
}

export interface PlantRobotSignalDetail {
  kind: 'RobotIOSignal'
  isStray: boolean
  signal: PlantRobotIOSignal
  controller: PlantRobotController | null
  readBy: PlantRobotRelationSite<PlantRobotJob>[]
  writtenBy: PlantRobotRelationSite<PlantRobotJob>[]
  /** Outbound `MAPS_TO_TAG` SCADA mappings (edge props preserved). */
  scadaTags: PlantPlcScadaTag[]
  /** Inbound `MAPS_TO_ROBOT_SIGNAL` from PLC tags. */
  plcMappings: PlantRobotPlcTagMapping[]
  /** Vision processes whose results a job writes onto this signal (`OUTPUTS_TO`). */
  visionOutputs: PlantVisionProcessLink[]
  /** Vision processes a job starts after waiting on this signal (`TRIGGERS`). */
  visionTriggers: PlantVisionProcessLink[]
}

export interface PlantRobotConditionDetail {
  kind: 'RobotCondition'
  isStray: boolean
  condition: PlantRobotCondition
  controller: PlantRobotController | null
  usedBy: PlantRobotRelationSite<PlantRobotJob>[]
}

export interface PlantVisionProcessDetail {
  kind: 'VisionProcess'
  isStray: boolean
  process: PlantVisionProcess
  controller: PlantRobotController | null
  usedBy: PlantRobotRelationSite<PlantRobotJob>[]
  outputs: PlantVisionSignalLink[]
  triggers: PlantVisionSignalLink[]
  visionData: PlantVisionDataRef<PlantVisionData>[]
}

export interface PlantVisionDataDetail {
  kind: 'VisionData'
  isStray: boolean
  data: PlantVisionData
  controller: PlantRobotController | null
  usedBy: PlantRobotRelationSite<PlantRobotJob>[]
  processes: PlantVisionDataRef<PlantVisionProcess>[]
}

export type PlantRobotNodeDetail =
  | PlantRobotControllerDetail
  | PlantRobotArmDetail
  | PlantRobotJobDetail
  | PlantRobotSignalDetail
  | PlantRobotConditionDetail
  | PlantVisionProcessDetail
  | PlantVisionDataDetail

// ─── PLC tab (Plant Viewer §6) ────────────────────────────
//
// Controllers, Programs, Routines, AOIs, PLC data types, and PLCTags
// share one tree-plus-detail view on `/plant/plc`. Schemas mirror
// `tools/ontology-generator/src/nodespec/plc.json`; extra fields land
// on the raw node spread via `extractNode`'s rest-properties pattern.

/** Routine implementation language per the `Routine.type` enum in plc.json. */
export type PlcRoutineType = 'RLL' | 'ST' | 'FBD' | 'SFC' | 'SC' | 'L5X'

/** PLCTag direction per the `PLCTag.direction` enum in plc.json. */
export type PlcTagDirection = 'INPUT' | 'OUTPUT' | 'IN_OUT'

export interface PlantPlcDecoratedMember {
  name: string
  dataType?: string
  data_type?: string
  radix?: string
}

/** Discriminator across the PLC node kinds the tab visualises. */
export type PlantPlcNodeKind =
  | 'Controller'
  | 'Program'
  | 'Routine'
  | 'AOI'
  | 'PLCDataType'
  | 'PLCDataTypeMember'
  | 'PLCTag'

/** `Controller` node — hosts Programs (`BELONGS_TO`) and AOIs (`BELONGS_TO`). */
export interface PlantController extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  type?: string
  vendor?: string
  firmware_version?: string
}

/** `Program` node — a PLC program inside a Controller. */
export interface PlantProgram extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  controller_name?: string
  type?: string
}

/** `Routine` node — an implementation routine inside a Program. */
export interface PlantRoutine extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  program_name?: string
  type?: PlcRoutineType
  /** Raw source text; populated for ST / SC / L5X routines. */
  source_code?: string
  /** Per-rung structured payload populated by the L5X / ACD parsers. */
  rungs?: Array<Record<string, unknown>>
}

/** `AOI` (Add-On Instruction) — reusable PLC logic, owned by a Controller. */
export interface PlantAOI extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  revision?: string
  description?: string
  parameters?: Array<Record<string, unknown>>
  source_code?: string
}

/** Controller-scoped PLC structured data type (`PLCDataType`). */
export interface PlantPLCDataType extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  description?: string
}

/** Named member of a `PLCDataType`. */
export interface PlantPLCDataTypeMember extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  data_type?: string
  description?: string
}

/** `PLCTag` node — a data point inside a Controller / Program / Routine / AOI. */
export interface PlantPLCTag extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  data_type?: string
  direction?: PlcTagDirection
  description?: string
  default_value?: string
  is_array?: boolean
  array_bounds?: number[]
  aoi_name?: string
  tag_type?: string
  constant?: boolean
  external_access?: string
  radix?: string
  alias_for?: string
  decorated_data_type?: string
  decorated_members?: PlantPlcDecoratedMember[] | string
}

/** `ControlPattern` node — a named pattern attached to a Controller via `HAS_PATTERN`. */
export interface PlantControlPattern extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  pattern_type?: string
  description?: string
}

/**
 * One row in the PLC navigation tree. The tree mixes node kinds —
 * Controllers are roots, Programs/AOIs/PLCDataTypes hang off a
 * Controller, and Routines hang off a Program. `parentId` is `null`
 * for roots so the left-rail can re-parent client-side with the same
 * buildTree pattern `ProcessTreeNav` uses.
 */
export interface PlantPlcTreeNode extends PlantEntityMeta {
  id: string
  parentId: string | null
  kind: Exclude<PlantPlcNodeKind, 'PLCTag' | 'PLCDataTypeMember'>
  name: string
  /**
   * Rail badge count. For Controller / Program / Routine / AOI this is
   * the recursive `HAS_TAG -> PLCTag` rollup. For `PLCDataType` it is
   * the direct `HAS_MEMBER -> PLCDataTypeMember` count. PLCTags and
   * members are not tree rows.
   */
  tagCount: number
  /** Routine implementation language; only set when `kind === 'Routine'`. */
  routineType?: PlcRoutineType
}

export interface PlantPlcTree {
  nodes: PlantPlcTreeNode[]
  /**
   * Total count of `PLCTag` nodes in the graph, computed
   * server-side from `MATCH (t:PLCTag)` rather than
   * by summing the per-container `tagCount`s (which only counts tags
   * reachable through a `HAS_TAG` edge from one of the four PLC
   * container kinds and so under-reports when tags exist without an
   * owner edge or with the edge on a node that isn't in the tree).
   */
  totalTagCount: number
}

/** PLCTag neighbour reachable via `TRIGGERS|GATES|INHIBITS|CONTROLS`. */
export interface PlantPlcTagInfluence {
  /** Relationship type (one of the four influence edges). */
  edge: 'TRIGGERS' | 'GATES' | 'INHIBITS' | 'CONTROLS'
  /** `true` when the current PLCTag is the source of the edge. */
  outgoing: boolean
  target: PlantPLCTag
}

/** Cross-layer SCADA `Tag` reachable from a PLCTag via `MAPS_TO_TAG`. */
export interface PlantPlcScadaTag {
  id: string
  _id: string
  name: string
  /** Mapping edge that surfaced this tag. */
  edge: 'MAPS_TO_TAG'
  /** Full relationship payload, including mapping properties and provenance. */
  relationship: PlantEdge
  provider_name?: string
  /** Canonical Ignition live-read path, usually `[Provider]Folder/Tag`. */
  tag_path?: string
  /** PLC-side path used by the linker, e.g. `AU_100_2.EnableOut`. */
  plc_tag_path?: string
  /**
   * `:TagProvider` id that exposes this `:Tag` (via `PROVIDES`), when
   * resolvable. Together with `host_id` it powers a deep-link into
   * the Compute host page's tag explorer (provider panel + tag pre-
   * selected). Optional because legacy tags imported before the
   * `TagProvider` mapper landed have no `PROVIDES` edge.
   */
  provider_id?: string
  /**
   * `:Compute` host id that owns the resolved `TagProvider` (via
   * `HAS_TAG_PROVIDER`). Optional for the same reason as
   * `provider_id`.
   */
  host_id?: string
  /** Live-data gateway URL from the owning Compute host. */
  host_url?: string
  /** Optional Ignition WebDev project hint from the owning Compute host. */
  project?: string
}

export interface PlantPlcControllerDetail {
  kind: 'Controller'
  isStray: boolean
  controller: PlantController
  programs: PlantProgram[]
  aois: PlantAOI[]
  dataTypes: PlantPLCDataType[]
  patterns: PlantControlPattern[]
  /**
   * Recursive count of distinct `PLCTag` nodes reachable from this
   * Controller via `HAS_TAG` edges on the Controller itself or on any
   * Program/AOI/Routine sitting underneath it (`BELONGS_TO*0..2`).
   * Matches the value shown on the matching tree row.
   */
  tagCount: number
  /**
   * OPC server / device `Compute` nodes that address this Controller via
   * `(:Compute)-[:MAPS_TO_CONTROLLER]->(:Controller)`. Established by
   * IP-to-controller mapping ingest.
   */
  mappedComputes: PlantComputeRef[]
}

export interface PlantPlcProgramDetail {
  kind: 'Program'
  isStray: boolean
  program: PlantProgram
  controller: PlantController | null
  routines: PlantRoutine[]
  /**
   * `PLCTag` nodes attached to this Program directly via `HAS_TAG`.
   * Excludes tags owned by Routines under the Program — those roll up
   * into `recursiveTagCount` and are reachable by clicking through a
   * Routine row.
   */
  tags: PlantPLCTag[]
  /**
   * Recursive count of distinct `PLCTag` nodes reachable from this
   * Program (the Program itself + every Routine via `BELONGS_TO*0..1`).
   * Matches the value shown on the matching tree row.
   */
  recursiveTagCount: number
  /**
   * Cross-layer SCADA `Tag` nodes reachable from each PLCTag listed in
   * `tags`, keyed by PLCTag id.
   */
  scadaByTagId: Record<string, PlantPlcScadaTag[]>
}

export interface PlantPlcControllerTagsArgs {
  nodeId: string
  /** Zero-indexed page. Server computes `SKIP = page * pageSize`. */
  page: number
  /** Defaults to 50 server-side. */
  pageSize?: number
}

/**
 * One server-side page of controller-scope tags. For a Controller this is
 * every directly owned tag; for a Program it is the subset referenced by
 * that Program's Routines.
 */
export interface PlantPlcControllerTagsPage {
  totalTags: number
  tags: PlantPLCTag[]
  scadaByTagId: Record<string, PlantPlcScadaTag[]>
}

export interface PlantPlcRoutineDetail {
  kind: 'Routine'
  isStray: boolean
  routine: PlantRoutine
  program: PlantProgram | null
  controller: PlantController | null
  tags: PlantPLCTag[]
  /**
   * Cross-layer SCADA `Tag` nodes reachable from each PLCTag listed in
   * `tags`, keyed by PLCTag id. See {@link PlantPlcControllerDetail}.
   */
  scadaByTagId: Record<string, PlantPlcScadaTag[]>
  /** Tags this routine reads via `READS_TAG`. */
  readsTags: PlantPLCTag[]
  /** Tags this routine writes via `WRITES_TAG`. */
  writesTags: PlantPLCTag[]
}

export interface PlantPlcAoiDetail {
  kind: 'AOI'
  isStray: boolean
  aoi: PlantAOI
  controller: PlantController | null
  tags: PlantPLCTag[]
  /** Count of `(AOI)-[:INSTANTIATES]->(PLCTag)` rows. */
  instantiatedTagCount: number
  /**
   * Cross-layer SCADA `Tag` nodes reachable from each PLCTag listed in
   * `tags`, keyed by PLCTag id. See {@link PlantPlcControllerDetail}.
   */
  scadaByTagId: Record<string, PlantPlcScadaTag[]>
}

/** Ignition UDT linked from a PLC data type via `MAPS_TO_TYPE`. */
export interface PlantPlcMappedUdt {
  id: string
  name: string
}

export interface PlantPlcDataTypeDetail {
  kind: 'PLCDataType'
  isStray: boolean
  dataType: PlantPLCDataType
  controller: PlantController | null
  members: PlantPLCDataTypeMember[]
  mappedUdt: PlantPlcMappedUdt | null
  /** PLCTags linked via inbound `OF_TYPE`. */
  tags: PlantPLCTag[]
}

export interface PlantPlcDataTypeMemberDetail {
  kind: 'PLCDataTypeMember'
  isStray: boolean
  member: PlantPLCDataTypeMember
  dataType: PlantPLCDataType | null
  controller: PlantController | null
}

export interface PlantPlcTagDetail {
  kind: 'PLCTag'
  isStray: boolean
  tag: PlantPLCTag
  /** Structured controller data type reached via `OF_TYPE`. */
  dataType: PlantPLCDataType | null
  /** Owning Controller / Program / Routine / AOI via `HAS_TAG`. */
  owner: {
    id: string
    name: string
    kind: Exclude<PlantPlcNodeKind, 'PLCTag' | 'PLCDataType' | 'PLCDataTypeMember'>
  } | null
  scadaTags: PlantPlcScadaTag[]
  influences: PlantPlcTagInfluence[]
  /** Routines that read this tag via inbound `READS_TAG`. */
  readByRoutines: PlantRoutine[]
  /** Routines that write this tag via inbound `WRITES_TAG`. */
  writtenByRoutines: PlantRoutine[]
  /** Outbound `CALCULATED_BY` rows — sources this tag is derived from. */
  calculatedFrom: PlantPlcTagDerivation[]
  /** Inbound `CALCULATED_BY` rows — tags derived from this one. */
  feeds: PlantPlcTagDerivation[]
  /** Outbound `MAPS_TO_ROBOT_SIGNAL` mappings. */
  robotSignals: PlantPlcRobotSignalMapping[]
}

/**
 * One `CALCULATED_BY` derivation site between PLCTags. Edge props
 * `via_routine_urn` / `via_rung_number` identify the ladder locus.
 */
export interface PlantPlcTagDerivation {
  tag: PlantPLCTag
  viaRoutineUrn?: string
  viaRungNumber?: number
}

/**
 * One `MAPS_TO_ROBOT_SIGNAL` row from a PLCTag onto a robot IO signal.
 */
export interface PlantPlcRobotSignalMapping {
  signal: PlantRobotIOSignal
  method?: string
  confidence?: number
  register?: string
  direction?: string
}

/**
 * Discriminated union returned by `getPlantPlcNodeDetail`. The right-
 * pane component switches on `kind` to pick the per-kind sub-view.
 */
export type PlantPlcNodeDetail =
  | PlantPlcControllerDetail
  | PlantPlcProgramDetail
  | PlantPlcRoutineDetail
  | PlantPlcAoiDetail
  | PlantPlcDataTypeDetail
  | PlantPlcDataTypeMemberDetail
  | PlantPlcTagDetail

// ─── Plant Viewer Alarms ────────────────────────────────────
//
// Alarms emitted by `mapTagAlarms` during ingest. One :Alarm node per
// configured alarm on a tag (standalone tags via tag-mapper; UDT-instance
// member tags via udt-builders). UDT-derived alarms also carry an
// inbound HAS_ALARM_DEFINITION edge from their parent UDTDefinition so
// the Plant View can group them as UDTDefinition > UDTInstance > full
// tag path; standalone tag alarms fall into an "Ungrouped" bucket in
// grouped mode and always
// appear in flat mode.

/**
 * Five-level Ignition alarm priority scale. Source of truth for the
 * Plant View priority filter; mirrors the `AlarmPriority` enum
 * generated from the ontology nodespec.
 */
export type PlantAlarmPriority = 'Diagnostic' | 'Low' | 'Medium' | 'High' | 'Critical'

export const PLANT_ALARM_PRIORITIES: readonly PlantAlarmPriority[] = [
  'Diagnostic',
  'Low',
  'Medium',
  'High',
  'Critical'
] as const

/**
 * One row returned by `getPlantAlarms`. Always carries the source tag
 * id + canonical tag path; for UDT-derived alarms `definition` and
 * `instance` are populated so the grouped view can bucket by
 * UDTDefinition > UDTInstance. Both are `null` for standalone tag
 * alarms (the "Ungrouped / No UDT" bucket).
 */
export interface PlantAlarm extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  priority: PlantAlarmPriority
  mode?: string
  enabled?: boolean
  setpoint_a?: number
  setpoint_a_binding?: string
  setpoint_b?: number
  setpoint_b_binding?: string
  inclusive_a?: boolean
  inclusive_b?: boolean
  deadband?: number
  deadband_mode?: string
  time_on_delay_seconds?: number
  time_off_delay_seconds?: number
  ack_mode?: string
  ack_notes_required?: boolean
  label?: string
  label_binding?: string
  display_path?: string
  display_path_binding?: string
  notes?: string
  any_change?: boolean
  shelving_allowed?: boolean
  active_pipeline?: string
  ack_pipeline?: string
  clear_pipeline?: string
  /** Canonical `[Provider]Folder/Tag` path of the source tag. */
  tag_path?: string
  /** Graph id of the source `:Tag`. */
  tag_id: string
  /**
   * Display name of the source tag (`Tag.name`), e.g. `Speed` for a
   * UDT-instance member or `Sensors/Temperature` for a standalone tag.
   */
  tag_name?: string
  /** Parent UDTDefinition for UDT-derived alarms; null for standalone. */
  definition: PlantAlarmDefinitionRef | null
  /** Parent UDTInstance for UDT-derived alarms; null for standalone. */
  instance: PlantAlarmInstanceRef | null
  /**
   * Resolved upstream gateway for this alarm — walks the
   * `Compute -[:HAS_TAG_PROVIDER]-> TagProvider -[:PROVIDES]-> Tag`
   * chain at ingest time. The Plant View uses this to fan out
   * `/live-data/alarms/active` calls (one per unique gateway) and
   * stitch live status back onto the configured alarm. `null` when the
   * source tag is not joined to a Compute (legacy backups, OPC-only
   * setups), in which case the alarm has no live-status surface.
   */
  gateway: PlantGatewayRef | null
}

export interface PlantAlarmDefinitionRef {
  id: string
  name: string
}

export interface PlantAlarmInstanceRef {
  id: string
  name: string
  tag_path?: string
}

export interface PlantAlarmsArgs {
  /**
   * Optional priority allow-list. When omitted, every priority is
   * returned. The endpoint always paginates server-side via the shared
   * drain helper so callers receive the complete result set.
   */
  priorities?: PlantAlarmPriority[]
}

/**
 * `getPlantAlarms` response. `alarms` is the flat list (priority-filtered
 * server-side when `priorities` is supplied); `groups` pre-buckets the
 * flat list by UDTDefinition > UDTInstance so the grouped view doesn't
 * have to re-group on every render. Standalone alarms (no inbound
 * HAS_ALARM_DEFINITION edge) are collected into `ungrouped` so the
 * UI can render an
 * "Ungrouped / No UDT" section consistent with the strays patterns
 * used elsewhere in Plant View.
 */
export interface PlantAlarmsIndex {
  alarms: PlantAlarm[]
  groups: PlantAlarmDefinitionGroup[]
  ungrouped: PlantAlarm[]
}

export interface PlantAlarmDefinitionGroup {
  definition: PlantAlarmDefinitionRef
  instances: PlantAlarmInstanceGroup[]
  alarmCount: number
}

export interface PlantAlarmInstanceGroup {
  instance: PlantAlarmInstanceRef
  alarms: PlantAlarm[]
}

// ─── Live alarm status (proxy-server, /live-data/alarms/active) ──

/**
 * Live alarm state reported by Ignition's `system.alarm.queryStatus`,
 * exposed through the proxy's `/live-data/alarms/active` route. Only
 * the active+cleared, ack'd / unack'd permutations are produced by the
 * gateway; an alarm that is fully cleared *and* acknowledged drops out
 * of the live result set entirely.
 */
export type PlantAlarmLiveState = 'ActiveUnacked' | 'ActiveAcked' | 'ClearUnacked' | 'ClearAcked'

/**
 * Tri-state filter shown on the Alarms tab: "All" lists every
 * configured alarm regardless of live status; "Active" narrows to
 * alarms currently reported as `Active*` by the gateway. The "active"
 * subset is then further refined by `PlantAlarmAckFilter`.
 */
export type PlantAlarmStatusFilter = 'all' | 'active'

/**
 * Acknowledgement sub-filter applied inside `status='active'`. `all`
 * keeps both ack'd and unack'd active alarms.
 */
export type PlantAlarmAckFilter = 'all' | 'unacked' | 'acked'

/**
 * One row from `/live-data/alarms/active` (which delegates to the
 * `getAlarms` Ignition WebDev endpoint). `source` is the Ignition
 * alarm source path, e.g. `prov:default:/tag:Pump/Pressure:/alm:HighP`;
 * the Plant View parses it to match back to a configured `PlantAlarm`
 * by (gateway_url, tag_path, alarm_name). `gateway_url` is stamped
 * on by the fan-out so callers can disambiguate alarms from multiple
 * gateways in a single merged result.
 */
export interface PlantActiveAlarm {
  /** Originating gateway URL — added by the client-side fan-out. */
  gateway_url: string
  /** Ignition alarm id (UUID-ish). */
  id: string
  /** Ignition alarm source path (`prov:<provider>:/tag:<path>:/alm:<name>`). */
  source: string
  /** Display path configured on the alarm; may be empty. */
  displayPath?: string
  /** Ignition alarm priority — passed through as a string. */
  priority?: string
  /** Current state reported by the gateway. */
  state: PlantAlarmLiveState
  /** Epoch ms when the alarm went active, if known. */
  eventTime?: number
  /** Epoch ms when the alarm was acknowledged, if applicable. */
  ackTime?: number
  /** Epoch ms when the alarm cleared, if applicable. */
  clearedTime?: number
}

/**
 * Args for `getPlantActiveAlarms`. Each entry produces one upstream
 * `/live-data/alarms/active?gateway_url=…&project=…` call; results are
 * merged client-side with `gateway_url` stamped onto each row.
 *
 * Passing an empty list short-circuits to an empty result (no network).
 */
export interface PlantActiveAlarmsArgs {
  gateways: PlantActiveAlarmsGateway[]
}

export interface PlantActiveAlarmsGateway {
  gateway_url: string
  project?: string
}

export interface PlantActiveAlarmsResponse {
  alarms: PlantActiveAlarm[]
  /**
   * Per-gateway errors collected from the fan-out so a single bad
   * gateway doesn't blank out the whole result. Keyed by `gateway_url`.
   */
  errors: Record<string, string>
}

// ─── DCS tab ──────────────────────────────────────────────

export type PlantDcsNodeKind =
  | 'DcsController'
  | 'Cee'
  | 'ControlModule'
  | 'FunctionBlock'
  | 'IoModule'
  | 'IoChannel'
  | 'ControlTag'
  | 'ControlAlarm'
  | 'ControlSetpoint'

export interface PlantDcsController extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  vendor?: string
  model?: string
  ip_address?: string
  comm_path?: string
  redundancy_role?: string
  kind?: string
  source_guid?: string
}

export interface PlantCee extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  ip_address?: string
  comm_path?: string
  kind?: string
  source_guid?: string
}

export interface PlantControlModule extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  module_type?: string
  description?: string
  ee_name?: string
  period?: string
  source_guid?: string
}

export interface PlantFunctionBlock extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  block_type?: string
  template_name?: string
  block_role?: string
  kind?: string
  source_guid?: string
}

export interface PlantIoModule extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  card_type?: string
  iolink_name?: string
  slot?: string
  source_guid?: string
}

export interface PlantIoChannel extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  channel_type?: string
  channel_num?: string
  slot_num?: string
  spare?: boolean
  source_guid?: string
}

export interface PlantControlTag extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  param?: string
  origin_block?: string
}

export interface PlantControlAlarm extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  alarm_type?: string
  priority?: string
}

export interface PlantControlSetpoint extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  value?: string
}

export interface PlantBlockType extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  class_name?: string
  comp_type?: string
  create_type?: string
}

export interface PlantBlockParamGroup extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  group_number?: number
  parent_group?: number
  param_names?: string[]
}

export interface PlantDcsWiredPeer {
  id: string
  name: string
  kind: 'FunctionBlock' | 'IoChannel'
  from_param?: string
  to_param?: string
  from_index?: number
  to_index?: number
  role?: string
  conn_id?: number
}

export interface PlantDcsTreeNode extends PlantEntityMeta {
  id: string
  parentId: string | null
  kind: Exclude<PlantDcsNodeKind, 'ControlTag' | 'ControlAlarm' | 'ControlSetpoint'>
  name: string
  /** Immediate child count used as the rail badge. */
  childCount: number
}

export interface PlantDcsTree {
  nodes: PlantDcsTreeNode[]
  totalControlTagCount: number
}

export interface PlantDcsControllerDetail {
  kind: 'DcsController'
  isStray: boolean
  controller: PlantDcsController
  cees: PlantCee[]
  ioModules: PlantIoModule[]
  commsBlocks: PlantFunctionBlock[]
  partner: PlantDcsController | null
  mappedComputes: PlantComputeRef[]
}

export interface PlantDcsCeeDetail {
  kind: 'Cee'
  isStray: boolean
  cee: PlantCee
  controller: PlantDcsController | null
  modules: PlantControlModule[]
  commsBlocks: PlantFunctionBlock[]
}

export interface PlantDcsControlModuleDetail {
  kind: 'ControlModule'
  isStray: boolean
  module: PlantControlModule
  cee: PlantCee | null
  controller: PlantDcsController | null
  functionBlocks: PlantFunctionBlock[]
  sequenceBlocks: PlantFunctionBlock[]
  tags: PlantControlTag[]
}

export interface PlantDcsFunctionBlockDetail {
  kind: 'FunctionBlock'
  isStray: boolean
  block: PlantFunctionBlock
  module: PlantControlModule | null
  parentBlock: PlantFunctionBlock | null
  parentCee: PlantCee | null
  parentController: PlantDcsController | null
  childBlocks: PlantFunctionBlock[]
  blockType: PlantBlockType | null
  derivedFrom: PlantBlockType | null
  paramGroups: PlantBlockParamGroup[]
  wiredTo: PlantDcsWiredPeer[]
}

export interface PlantDcsIoModuleDetail {
  kind: 'IoModule'
  isStray: boolean
  ioModule: PlantIoModule
  controller: PlantDcsController | null
  channels: PlantIoChannel[]
}

export interface PlantDcsIoChannelDetail {
  kind: 'IoChannel'
  isStray: boolean
  channel: PlantIoChannel
  ioModule: PlantIoModule | null
  wiredFrom: PlantFunctionBlock[]
}

export interface PlantDcsControlTagDetail {
  kind: 'ControlTag'
  isStray: boolean
  tag: PlantControlTag
  module: PlantControlModule | null
  alarms: PlantControlAlarm[]
  setpoint: PlantControlSetpoint | null
}

export interface PlantDcsControlAlarmDetail {
  kind: 'ControlAlarm'
  isStray: boolean
  alarm: PlantControlAlarm
  tag: PlantControlTag | null
}

export interface PlantDcsControlSetpointDetail {
  kind: 'ControlSetpoint'
  isStray: boolean
  setpoint: PlantControlSetpoint
  tag: PlantControlTag | null
}

export type PlantDcsNodeDetail =
  | PlantDcsControllerDetail
  | PlantDcsCeeDetail
  | PlantDcsControlModuleDetail
  | PlantDcsFunctionBlockDetail
  | PlantDcsIoModuleDetail
  | PlantDcsIoChannelDetail
  | PlantDcsControlTagDetail
  | PlantDcsControlAlarmDetail
  | PlantDcsControlSetpointDetail

// ─── Network tab (Experion EMDB enterprise / PCN) ─────────

/** Tree-row kinds on the Network tab (Compute is cross-linked, not a row). */
export type PlantNetworkNodeKind = 'Asset' | 'NetworkSegment' | 'NetworkDevice'

export interface PlantAsset extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  full_item_name?: string
  description?: string
  source_guid?: string
  /** Decoded EMDB enum label (e.g. SEGMENTTYPE), when present. */
  kind?: string
}

export interface PlantNetworkSegment extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  /** `segment` | `fte_community`. */
  segment_kind?: string
  description?: string
  source_guid?: string
  kind?: string
}

export interface PlantNetworkDevice extends PlantEntityMeta {
  id: string
  _id: string
  name: string
  display_name?: string
  /** `switch` | `fte_device` | `net_equip` | `other`. */
  device_kind?: string
  ip_address?: string
  description?: string
  source_guid?: string
  kind?: string
}

export interface PlantNetworkTreeNode extends PlantEntityMeta {
  id: string
  parentId: string | null
  kind: PlantNetworkNodeKind
  name: string
  /** Experion `EntityName` when ingest stamped it; the rail prefers this over `name`. */
  display_name?: string
  /** Immediate tree-child count used as the rail badge. */
  childCount: number
  projectId?: string | null
  projectName?: string | null
}

export interface PlantNetworkTree {
  nodes: PlantNetworkTreeNode[]
}

/** Containing Asset / NetworkSegment / NetworkDevice reached via CONTAINS. */
export interface PlantNetworkParentRef {
  id: string
  name: string
  kind: PlantNetworkNodeKind
}

/** The owning Site when a node hangs directly off it. */
export interface PlantNetworkSiteRef {
  id: string
  name: string
}

/** Experion EMDB System Management folder (`EmdbProject`). */
export interface PlantNetworkProjectRef {
  id: string
  name: string
}

export interface PlantAssetDetail {
  kind: 'Asset'
  isStray: boolean
  asset: PlantAsset
  parent: PlantNetworkParentRef | null
  siteParent: PlantNetworkSiteRef | null
  project: PlantNetworkProjectRef | null
  childAssets: PlantAsset[]
  segments: PlantNetworkSegment[]
  devices: PlantNetworkDevice[]
  computes: PlantComputeRef[]
}

export interface PlantNetworkSegmentDetail {
  kind: 'NetworkSegment'
  isStray: boolean
  segment: PlantNetworkSegment
  parent: PlantNetworkParentRef | null
  siteParent: PlantNetworkSiteRef | null
  project: PlantNetworkProjectRef | null
  devices: PlantNetworkDevice[]
  computes: PlantComputeRef[]
}

export interface PlantNetworkDeviceDetail {
  kind: 'NetworkDevice'
  isStray: boolean
  device: PlantNetworkDevice
  parent: PlantNetworkParentRef | null
  siteParent: PlantNetworkSiteRef | null
  project: PlantNetworkProjectRef | null
  /** Child NetworkDevices (a device may contain further devices). */
  devices: PlantNetworkDevice[]
  computes: PlantComputeRef[]
}

export type PlantNetworkNodeDetail =
  | PlantAssetDetail
  | PlantNetworkSegmentDetail
  | PlantNetworkDeviceDetail
